import { geolocateWithRetry } from './utils/geolocation';
import React, { useState, useEffect, lazy, Suspense, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import MapLibreMap from './components/MapLibreMap';
import MapLibreMarker from './components/MapLibreMarker';
import MapErrorBoundary from './components/MapErrorBoundary';
import FloatingLegend from './components/FloatingLegend';
import DirectionalArrow from './components/DirectionalArrow';
import EmojiGif from './components/EmojiGif';
import { buildApiUrl } from './config/environment';
import { RESTAURANT_IMG, MAP_IMG, FEATURE_IMAGES } from './utils/staticData';
import Navigation from './components/Navigation';
import { getTodayOpeningHours, formatTodayHours, getOpenStatusClasses, getOpenStatusText } from './utils/openingHours';
import { calculateOptimalZoom, restorePreviousViewport } from './utils/mapUtils';
import { useContactForm } from './hooks/useContactForm';
import { useModalManager } from './hooks/useModalManager';
import { useSettings } from './hooks/useSettings';
import { useAuth } from './hooks/useAuth';
import { useLanguageManagement } from './hooks/useLanguageManagement';
import { setGlobalLanguage } from './hooks/useTranslations';
import { useResponsiveLayout } from './hooks/useResponsiveLayout';
import { useSubscriptionState } from './hooks/useSubscriptionState';
import { useProximityNotifications } from './hooks/useProximityNotifications';
import { ResponsiveMapLayout } from './components/ResponsiveMapLayout';
import CookieConsentModal from './components/CookieConsentModal';
import ChargerSearchModal from './components/ChargerSearchModal';
import { useCookieConsent } from './services/cookieConsentService';
import { 
  cleanTeslaName, 
  getNetworks as getNetworksUtil, 
  openTeslaNavigation,
  openGoogleMapsNavigation
} from './utils/appUtils';
import { authService } from './services/authService';
import { settingsService } from './services/settingsService';
import { apiService } from './services/apiService';
import { calculateDistance, calculateBearing } from './utils/distanceUtils';
import { 
  MapPinIcon, 
  BoltIcon, 
  BuildingStorefrontIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

// Lazy load modal components
const LoginModal = lazy(() => import('./components/LoginModal'));
const RegisterModal = lazy(() => import('./components/RegisterModal'));
const SettingsModal = lazy(() => import('./components/ModernSettingsModal'));
const AboutModal = lazy(() => import('./components/AboutModal'));
const PricingModal = lazy(() => import('./components/PricingModal'));
const SuggestedMealModal = lazy(() => import('./components/SuggestedMealModal'));
const FAQModal = lazy(() => import('./components/FAQModal'));
const ContactModal = lazy(() => import('./components/ContactModal'));
const DisclaimerModal = lazy(() => import('./components/DisclaimerModal'));
const LearnMoreModal = lazy(() => import('./components/LearnMoreModal'));
const RestaurantPreferencesModal = lazy(() => import('./components/RestaurantPreferencesModal'));
const SubscriptionManagementModal = lazy(() => import('./components/SubscriptionManagementModal'));
const TierChangeConfirmationModal = lazy(() => import('./components/TierChangeConfirmationModal'));
const ReportNonRestaurantModal = lazy(() => import('./components/ReportNonRestaurantModal'));
const ProximityNotificationStack = lazy(() => import('./components/ProximityNotificationStack'));



function App() {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [chargersLoading, setChargersLoading] = useState(false);
  const [superchargers, setSuperchargers] = useState<any[]>([]);
  const [viewport, setViewport] = useState<any | null>(null);
  
  const [selectedCharger, setSelectedCharger] = useState<any>(null);
  const [nearbyRestaurants, setNearbyRestaurants] = useState<any[]>([]);
  // IDs of chargers that have a nearby restaurant matching the user's
  // favourite chains/keywords (computed cache-only by the backend).
  const [favoriteChargerIds, setFavoriteChargerIds] = useState<Set<any>>(new Set());
  const [chargerAvailability, setChargerAvailability] = useState<any>(null);
  const [selectedNetworks, setSelectedNetworks] = useState([23, 3534, 27]); // Default to all three Tesla operator IDs
  const [userLocation, setUserLocation] = useState<{latitude: number; longitude: number; heading?: number} | null>(null);
  const [suggestedMealPreference, setSuggestedMealPreference] = useState<'normal' | 'nicer' | 'cheaper'>('normal');
  const [suggestedMealResults, setSuggestedMealResults] = useState<any[]>([]);
  const [suggestedMealLoading, setSuggestedMealLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Authentication management
  const {
    isAuthenticated,
    user,
    authError,
    setUser,
    loginForm,
    setLoginForm,
    registerForm,
    setRegisterForm,
    couponValidation,
    setCouponValidation,
    checkAuthStatus,
    handleLogin,
    handleRegister,
    handleLogout,
    validateCoupon,
    resetAuthError,
    resetForms,
  } = useAuth();
  
  // Subscription management
  const {
    userSubscription,
    availableTiers,
    isLoadingSubscription,
    setIsLoadingSubscription,
    fetchUserSubscription,
    fetchAvailableTiers,
  } = useSubscriptionState();
  
  // Language management
  const {
    language,
    setLanguage,
    t,
    loadUserLanguage,
    saveUserLanguage,
  } = useLanguageManagement(isAuthenticated, user);

  // Keep the shared translation store (used by all useTranslations() components)
  // in sync with the app's authoritative language, whatever changes it.
  useEffect(() => { setGlobalLanguage(language); }, [language]);

  // Settings management
  const {
    settings,
    setSettings,
    settingsError,
    priceRange,
    maxRestaurants,
    subscriptionInfo,
    loadSettings,
    saveSettings,
    loadSubscriptionInfo,
    loadPriceRange,
    loadMaxRestaurants,
    updatePriceRange,
    updateMaxRestaurants,
  } = useSettings(isAuthenticated);

  // Ask the backend which of the loaded chargers have a nearby favourite
  // restaurant (cache-only). Re-runs whenever the charger set changes.
  useEffect(() => {
    if (!isAuthenticated || !superchargers || superchargers.length === 0) {
      setFavoriteChargerIds(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(buildApiUrl('api/chargers/favorites'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            chargers: superchargers
              .filter((s: any) => s && s.id != null && s.latitude != null && s.longitude != null)
              .map((s: any) => ({ id: s.id, lat: s.latitude, lng: s.longitude })),
          }),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled && Array.isArray(data.favoriteChargerIds)) {
          setFavoriteChargerIds(new Set(data.favoriteChargerIds));
        }
      } catch (e) {
        /* non-fatal: no hearts if this fails */
      }
    })();
    return () => { cancelled = true; };
  }, [superchargers, isAuthenticated]);

  // User restaurant preferences state
  const [userPreferences, setUserPreferences] = useState<{
    preferred_chains: string[];
    keywords: string[];
    search_level: string;
  } | null>(null);

  // Modal management
  const {
    showLoginModal,
    showRegisterModal,
    showSettingsModal,
    showAboutModal,
    showPricingModal,
    showFAQModal,
    showContactModal,
    showLearnMoreModal,
    showDisclaimerModal,
    showRestaurantPreferencesModal,
    showSubscriptionManagementModal,
    showTierChangeConfirmationModal,
    showSuggestedMealModal,
    showReportNonRestaurantModal,
    suggestedMealMode,
    openLoginModal,
    openRegisterModal,
    openSettingsModal,
    openAboutModal,
    openPricingModal,
    openFAQModal,
    openContactModal,
    openLearnMoreModal,
    openDisclaimerModal,
    openRestaurantPreferencesModal,
    openSubscriptionManagementModal,
    openTierChangeConfirmationModal,
    openSuggestedMealModal,
    openReportNonRestaurantModal,
    enterSuggestedMealMode,
    exitSuggestedMealMode,
    closeLoginModal,
    closeRegisterModal,
    closeSettingsModal,
    closeAboutModal,
    closePricingModal,
    closeFAQModal,
    closeContactModal,
    closeLearnMoreModal,
    closeDisclaimerModal,
    closeRestaurantPreferencesModal,
    closeSubscriptionManagementModal,
    closeTierChangeConfirmationModal,
    closeSuggestedMealModal,
    closeReportNonRestaurantModal,
  } = useModalManager();
  
  // Cookie consent management
  const {
    showModal: showCookieConsentModal,
    hasAccepted: hasAcceptedCookies,
    hasDeclined: hasDeclinedCookies,
    acceptCookies,
    declineCookies
  } = useCookieConsent();
  
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [pendingTierChange, setPendingTierChange] = useState<{
    currentTier: any;
    newTier: any;
    isUpgrade: boolean;
  } | null>(null);
  const [isTierChangeLoading, setIsTierChangeLoading] = useState(false);
  const [chargerSearchModalOpen, setChargerSearchModalOpen] = useState(false);

  // Handle tier change request
  const handleTierChangeRequest = (newTierName: string) => {
    const currentTier = availableTiers.find(tier => tier.id === userSubscription?.tier) || availableTiers[0];
    const newTier = availableTiers.find(tier => tier.id === newTierName);
    
    if (!newTier || !currentTier) return;
    
    const currentTierIndex = availableTiers.findIndex(t => t.id === currentTier.id);
    const newTierIndex = availableTiers.findIndex(t => t.id === newTier.id);
    const isUpgrade = newTierIndex > currentTierIndex;
    
    setPendingTierChange({
      currentTier,
      newTier,
      isUpgrade
    });
    
    openTierChangeConfirmationModal();
  };

  // Handle tier change confirmation
  const handleTierChangeConfirm = async () => {
    if (!pendingTierChange) return;
    
    setIsTierChangeLoading(true);
    
    try {
      const response = await fetch('/api/subscription/change-tier', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // Use cookie-based authentication instead of JWT
        body: JSON.stringify({
          newTier: pendingTierChange.newTier.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change subscription tier');
      }

      // Refresh subscription status
      await fetchUserSubscription();
      
      // Close modals and reset state
      closeTierChangeConfirmationModal();
      closeSubscriptionManagementModal();
      setPendingTierChange(null);
      
      // Show success message (could be improved with a toast notification)
      alert(`${pendingTierChange.isUpgrade ? t('app_successfullyUpgradedTo') : t('app_successfullyDowngradedTo')} ${pendingTierChange.newTier.displayName}!`);
      
    } catch (error) {
      console.error('Tier change error:', error);
      alert(`${t('app_failedToChangeSubscription')}: ${error instanceof Error ? error.message : t('app_unknownError')}`);
    } finally {
      setIsTierChangeLoading(false);
    }
  };
  
  // User location now uses DirectionalArrow component
  
  // Contact form hook
  const { contactForm, setContactForm, contactStatus, handleContactSubmit } = useContactForm({
    isAuthenticated,
    user,
    onSuccess: closeContactModal
  });
  
  // Responsive layout management
  const {
    viewportType,
    isMobile,
    isTablet,
    isDesktop,
    getMapContainerProps,
    getLegendContainerProps,
    getMainContainerProps
  } = useResponsiveLayout();
  
  // Map reference for controlling map behavior
  const mapRef = useRef<any>(null);

  // Geolocation watch id — kept in a ref so it can be cleared on unmount.
  // Previously the watch ran forever (leaking GPS + firing setState on every
  // fix), which piled up re-renders and crashed constrained browsers.
  const watchIdRef = useRef<number | null>(null);
  
  // Handler for "Show Me" button in proximity notifications
  const handleNavigateToCharger = useCallback(async (chargerId: string) => {
    const charger = superchargers.find(sc => sc.id === chargerId);
    if (charger) {
      console.log('🗺️ Navigating to supercharger:', charger.name);
      
      // Animate to the supercharger with proper zoom
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [charger.longitude, charger.latitude],
          zoom: 13,
          duration: 600
        });
      }
      
      // Set selected charger immediately for UI feedback
      setSelectedCharger(charger);
      
      try {
        // Fetch restaurants (same logic as normal supercharger selection)
        const tierLimits = userSubscription ? {
          maxRestaurants: userSubscription.tier === 'free' ? 3 : userSubscription.tier === 'basic' ? 10 : userSubscription.tier === 'bronze' ? 10 : userSubscription.tier === 'silver' ? 25 : -1,
          maxSearchRadius: 200.0
        } : { maxRestaurants: 3, maxSearchRadius: 200.0 };
        
        const apiUrl = buildApiUrl(`api/places/superchargers/${charger.id}/restaurants?lat=${charger.latitude}&lng=${charger.longitude}&maxResults=${tierLimits.maxRestaurants > 0 ? tierLimits.maxRestaurants : 100}&radius=${tierLimits.maxSearchRadius}`);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'  // Use cookie-based authentication instead of JWT
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.count !== undefined) {
          // Not authenticated - show count only
          setNearbyRestaurants([]);
          setSelectedCharger({ ...charger, restaurantCount: data.count });
        } else {
          // Authenticated - show full restaurant data
          let filteredAndSortedRestaurants = data
            .filter((restaurant: any) => restaurant.name)
            .sort((a: any, b: any) => {
              const ratingA = a.rating || 0;
              const ratingB = b.rating || 0;
              return ratingB - ratingA;
            });
          
          // Apply tier limits
          if (tierLimits.maxRestaurants > 0) {
            filteredAndSortedRestaurants = filteredAndSortedRestaurants.slice(0, tierLimits.maxRestaurants);
          }
          
          setNearbyRestaurants(filteredAndSortedRestaurants);
          setSelectedCharger(charger);
          
          console.log(`🍽️ Loaded ${filteredAndSortedRestaurants.length} restaurants for ${charger.name}`);
        }
        
      } catch (error) {
        console.error('Error fetching restaurants for Show Me:', error);
        setNearbyRestaurants([]);
        setSelectedCharger(charger);
      }
      
      // Also fetch availability data in parallel
      // Use numberOfPoints directly from charger data
      const availability = {
        total: charger.numberOfPoints || null,
        available: null,
        lastUpdated: new Date().toISOString()
      };
      console.log(`🔋 Using numberOfPoints from charger data: ${availability.total} stalls`);
      setChargerAvailability(availability);
    } else {
      console.error('🗺️ Charger not found for ID:', chargerId);
    }
  }, [superchargers, userSubscription]);

  // Collapsed notifications state
  const [collapsedNotifications, setCollapsedNotifications] = useState<Set<string>>(new Set());

  // Toggle collapse state for notifications
  const toggleNotificationCollapse = useCallback((notificationId: string) => {
    setCollapsedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  }, []);

  // Proximity notifications for tablet and desktop when map mode is active
  const {
    notificationState: proximityNotifications,
    setRestaurantFetcher,
    dismissNotification: dismissProximityNotification,
    navigateToCharger: navigateToProximityCharger,
    waitOneHour: waitOneHourProximity,
    waitTwoHours: waitTwoHoursProximity,
    turnOffNotifications: turnOffProximityNotifications,
    enableNotifications: enableProximityNotifications
  } = useProximityNotifications(
    userLocation,
    superchargers,
    isTablet || isDesktop,
    handleNavigateToCharger
  );
  
  // Improved distance tracking for auto-removal of notifications with 10km threshold
  const [notificationDistances, setNotificationDistances] = useState<Map<string, {
    distances: number[];          // Last 5 distances
    timestamps: number[];         // Corresponding timestamps  
    bearings: number[];          // Direction from user to charger
    hasBeenWithin10km: boolean;  // Was user ever within 10km?
    minDistance: number;         // Closest approach
    isMovingAway: boolean;       // Current movement direction
  }>>(new Map());

  // Improved velocity-based move-away detection with 10km threshold
  useEffect(() => {
    if (!userLocation || proximityNotifications.notifications.length === 0) return;

    const now = Date.now();
    const newDistances = new Map(notificationDistances);
    
    proximityNotifications.notifications.forEach(notification => {
      // Find the current charger data from the main superchargers list
      const currentCharger = superchargers.find(charger => charger.id === notification.supercharger.id);
      if (!currentCharger) {
        console.warn(`⚠️ Charger ID ${notification.supercharger.id} not found in superchargers list. Available IDs:`, superchargers.map(s => s.id));
        return;
      }

      // Calculate accurate distance using current charger coordinates
      const currentDistance = userLocation ? 
        calculateDistance(
          userLocation,
          {
            latitude: currentCharger.latitude,
            longitude: currentCharger.longitude
          }
        ) : notification.supercharger.distance;
      
      // Calculate bearing from user to charger (for direction analysis)
      const bearing = calculateBearing(userLocation, {
        latitude: currentCharger.latitude,
        longitude: currentCharger.longitude
      });
      
      const chargerKey = notification.supercharger.id;
      const existingData = newDistances.get(chargerKey);
      
      
      if (existingData) {
        // Update tracking arrays (keep ALL measurements for 30-second algorithm)
        const updatedDistances = [...existingData.distances, currentDistance];
        const updatedTimestamps = [...existingData.timestamps, now];
        const updatedBearings = [...existingData.bearings, bearing];
        
        const newMinDistance = Math.min(existingData.minDistance, currentDistance);
        const newHasBeenWithin10km = existingData.hasBeenWithin10km || currentDistance < 10.0;
        
        // Simple sliding timeline: 15s intervals (spot1: 30s ago, spot3: 15s ago, spot5: now)
        let spot1Distance = null, spot3Distance = null, spot5Distance = null;
        let spot1Time = null, spot3Time = null, spot5Time = null;
        
        // Find the most recent measurement that is at least 30s old (this becomes spot1)
        let spot1Index = -1;
        for (let i = updatedDistances.length - 1; i >= 0; i--) {
          const ageInSeconds = (now - updatedTimestamps[i]) / 1000;
          if (ageInSeconds >= 30) {
            spot1Index = i;
            spot1Distance = updatedDistances[i];
            spot1Time = updatedTimestamps[i];
            break;
          }
        }
        
        // Find the most recent measurement that is at least 15s old (this becomes spot3)
        let spot3Index = -1;
        if (spot1Distance !== null) {
          for (let i = updatedDistances.length - 1; i >= 0; i--) {
            const ageInSeconds = (now - updatedTimestamps[i]) / 1000;
            if (ageInSeconds >= 15 && i > spot1Index) {
              spot3Index = i;
              spot3Distance = updatedDistances[i];
              spot3Time = updatedTimestamps[i];
              break;
            }
          }
        }
        
        // The most recent measurement becomes spot5
        if (spot3Distance !== null && updatedDistances.length > 0) {
          spot5Distance = currentDistance;
          spot5Time = now;
        }
        
        const shouldAutoRemove = spot1Distance !== null && spot3Distance !== null && spot5Distance !== null &&
          spot5Distance > spot3Distance && spot3Distance > spot1Distance;
        
        
        if (shouldAutoRemove) {
          dismissProximityNotification(notification.id);
          newDistances.delete(chargerKey);
          setCollapsedNotifications(prev => {
            const newSet = new Set(prev);
            newSet.delete(notification.id);
            return newSet;
          });
          return;
        }
        
        // Update tracking data with all measurements
        newDistances.set(chargerKey, {
          distances: updatedDistances,
          timestamps: updatedTimestamps, 
          bearings: updatedBearings,
          hasBeenWithin10km: newHasBeenWithin10km,
          minDistance: newMinDistance,
          isMovingAway: false
        });
      } else {
        // First time tracking this charger
        newDistances.set(chargerKey, {
          distances: [currentDistance],
          timestamps: [now],
          bearings: [bearing],
          hasBeenWithin10km: currentDistance < 10.0,
          minDistance: currentDistance,
          isMovingAway: false
        });
      }
    });
    
    setNotificationDistances(newDistances);
  }, [userLocation, proximityNotifications.notifications, dismissProximityNotification]);

  const [selectedRestaurantIndex, setSelectedRestaurantIndex] = useState<number | null>(null);
  const [restaurantToReport, setRestaurantToReport] = useState<any>(null);
  
  // Handle non-restaurant report submission
  const handleReportNonRestaurant = (restaurant: any) => {
    setRestaurantToReport(restaurant);
    openReportNonRestaurantModal();
  };

  const submitNonRestaurantReport = async (restaurant: any, reason: string) => {
    if (!isAuthenticated || !restaurant) return;

    try {
      const response = await fetch(buildApiUrl('api/report-non-restaurant'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          place_id: restaurant.place_id,
          name: restaurant.name,
          address: restaurant.address || restaurant.formatted_address || '',
          reason: reason || '',
          reported_by: user?.username || user?.displayName || 'Unknown'
        })
      });

      if (response.ok) {
        alert(t('app_reportThankYou'));
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        
        if (response.status === 409) {
          alert(t('app_reportAlreadyReported'));
        } else {
          alert(errorData.error || t('app_reportError'));
        }
        return;
      }
    } catch (error) {
      console.error('Error submitting non-restaurant report:', error);
      alert(t('app_reportError'));
    }
  };
  
  // Restaurant fetching function for proximity notifications
  const fetchRestaurantsForCharger = async (chargerIdOrObject: string | any): Promise<any[]> => {
    try {
      let supercharger;
      
      // Handle both ID (string) and supercharger object
      if (typeof chargerIdOrObject === 'string') {
        const chargerId = chargerIdOrObject;
        
        // Find the supercharger by ID - check both string and number formats
        supercharger = superchargers.find(sc => sc.id === chargerId || sc.id === parseInt(chargerId));
        
        // If supercharger not found, wait and retry once (handle race condition)
        if (!supercharger && superchargers.length === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          // Re-check after waiting
          const updatedSuperchargers = superchargers;
          supercharger = updatedSuperchargers.find(sc => sc.id === chargerId || sc.id === parseInt(chargerId));
        }
        
        if (!supercharger) {
          console.error('Supercharger not found for proximity notification:', chargerId);
          return [];
        }
      } else {
        // Supercharger object passed directly (preferred for proximity notifications)
        supercharger = chargerIdOrObject;
      }

      // Get user's tier limits for restaurants
      const tierLimits = userSubscription ? {
        maxRestaurants: userSubscription.tier === 'free' ? 3 : userSubscription.tier === 'basic' ? 10 : userSubscription.tier === 'bronze' ? 10 : userSubscription.tier === 'silver' ? 25 : -1,
        maxSearchRadius: userSubscription.tier === 'free' ? 1.0 : userSubscription.tier === 'basic' ? 2.0 : userSubscription.tier === 'bronze' ? 2.0 : userSubscription.tier === 'silver' ? 5.0 : 10.0
      } : { maxRestaurants: 3, maxSearchRadius: 200.0 };

      const apiUrl = buildApiUrl(`api/places/superchargers/${supercharger.id}/restaurants?lat=${supercharger.latitude}&lng=${supercharger.longitude}&maxResults=${tierLimits.maxRestaurants > 0 ? tierLimits.maxRestaurants : 100}&radius=${tierLimits.maxSearchRadius}`);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.message && data.message.includes('Login required')) {
        return [];
      } else if (Array.isArray(data)) {
        // Authenticated - return filtered and sorted restaurants
        let filteredRestaurants = data
          .filter((restaurant: any) => restaurant.name)
          .sort((a: any, b: any) => {
            const ratingA = a.rating || 0;
            const ratingB = b.rating || 0;
            return ratingB - ratingA;
          });

        if (tierLimits.maxRestaurants > 0) {
          filteredRestaurants = filteredRestaurants.slice(0, tierLimits.maxRestaurants);
        }

        return filteredRestaurants;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching restaurants for proximity notification:', error);
      return [];
    }
  };

  // Set up the restaurant fetcher for proximity notifications
  useEffect(() => {
    setRestaurantFetcher(fetchRestaurantsForCharger);
  }, [setRestaurantFetcher, superchargers, userSubscription]);
  
  const restaurantListRef = useRef<HTMLDivElement>(null);
  
  // Store previous viewport to restore when popup is closed
  const [previousViewport, setPreviousViewport] = useState<{
    longitude: number;
    latitude: number;
    zoom: number;
  } | null>(null);
  
  // MapLibre GL JS ready - no token needed for OpenStreetMap
  const [mapboxToken, setMapboxToken] = useState<string | null>('ready');
  






  // Persistent photo URL cache to prevent flickering when map moves
  const photoUrlCacheRef = React.useRef<{ [key: string]: string }>({});
  
  // Clean up old photo URLs when restaurants change to prevent memory leaks
  React.useEffect(() => {
    if (nearbyRestaurants.length === 0) {
      // Clear cache when no restaurants (new supercharger selected)
      photoUrlCacheRef.current = {};
    }
  }, [nearbyRestaurants.length === 0]);
  
  // Helper to get stable photo URL - uses persistent cache
  const getRestaurantPhotoUrl = (restaurant: any) => {
    if (!restaurant.photos || restaurant.photos.length === 0) return null;
    
    const key = `${restaurant.place_id || restaurant.name}_${restaurant.photos[0].photo_reference}`;
    
    // Return cached URL if it exists
    if (photoUrlCacheRef.current[key]) {
      return photoUrlCacheRef.current[key];
    }
    
    // Generate and cache the URL if not cached yet
    const url = `${buildApiUrl(`api/photos/${restaurant.photos[0].photo_reference}?maxwidth=300&placeId=${restaurant.place_id || ''}&restaurantName=${encodeURIComponent(restaurant.name)}`)}`;
    photoUrlCacheRef.current[key] = url;
    return url;
  };

  // Restaurant Card Component
  const RestaurantCard = ({ restaurant, index, onRestaurantClick, isExpanded, onToggleExpanded }: {
    restaurant: any;
    index: number;
    onRestaurantClick: (restaurant: any) => void;
    isExpanded: boolean;
    onToggleExpanded: (index: number) => void;
  }) => {
    // Get proper opening hours data (never assumes open/closed without valid data)
    const openingHoursData = getTodayOpeningHours(restaurant.opening_hours);
    return (
      <div className="restaurant-card bg-white border border-gray-200 rounded-lg mb-3 shadow-sm hover:shadow-md transition-shadow">
        <div 
          className="p-3 cursor-pointer"
          onClick={() => {
            // Restaurant card clicked
            onToggleExpanded(index);
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="bg-blue-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                {String.fromCharCode(65 + index)}
              </span>
              <div>
                <h5 className="font-semibold text-gray-900 text-sm truncate">{restaurant.name.length > 32 ? restaurant.name.substring(0, 32) + '...' : restaurant.name}</h5>
                <p className="text-xs text-gray-600">
                  {restaurant.distance_km
                    ? `${restaurant.distance_km.toFixed(1)}km ${t('app_away')}`
                    : `20m ${t('app_away')}`
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-yellow-600 flex items-center">
                <EmojiGif emoji="⭐" size="xs" className="mr-1" />
                {restaurant.rating}
              </span>
              <span className="text-xs text-green-600 flex items-center">
                {Array.from({ length: restaurant.price_level == null || restaurant.price_level === undefined ? 1 : (restaurant.price_level <= 1 ? 1 : restaurant.price_level) }).map((_, i) => (
                  <EmojiGif key={i} emoji="💰" size="xs" className="mr-0.5" />
                ))}
              </span>
              {/* Opening hours display - NEVER assumes open/closed without valid data */}
              {openingHoursData.hasValidData ? (
                <div className="text-xs">
                  <div className="text-gray-600 mb-1">{formatTodayHours(openingHoursData.todayHours)}</div>
                  {openingHoursData.isCurrentlyOpen !== null && (
                    <span className={`px-2 py-1 rounded ${getOpenStatusClasses(openingHoursData.isCurrentlyOpen)}`}>
                      {getOpenStatusText(openingHoursData.isCurrentlyOpen)}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                  {t('app_hoursNotAvailable')}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-3 bg-gray-50">
            {/* Restaurant Image */}
            {restaurant.photos && restaurant.photos.length > 0 && getRestaurantPhotoUrl(restaurant) && (
              <div className="mb-3">
                <img 
                  src={getRestaurantPhotoUrl(restaurant)!}
                  alt={restaurant.name}
                  className="w-full h-32 object-cover rounded-md"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            
            {/* Restaurant Details */}
            <div className="space-y-2 mb-3">
              <p className="text-xs text-gray-700">{restaurant.address}</p>
              {restaurant.phone && (
                <a href={`tel:${restaurant.phone}`} className="text-xs text-blue-600 hover:underline block">
                  📞 {restaurant.phone}
                </a>
              )}
              {restaurant.website && (
                <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">
                  🌐 {t('app_website')}
                </a>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-2">
              {restaurant.latitude && restaurant.longitude && (
                <>
                  <button
                    onClick={() => openTeslaNavigation(restaurant.latitude, restaurant.longitude, restaurant.name)}
                    className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                  >
                    🚗 Tesla
                  </button>
                  <button
                    onClick={() => openGoogleMapsNavigation(restaurant.latitude, restaurant.longitude, restaurant.name)}
                    className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200"
                  >
                    🧭 {t('app_drive')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };


  // Map cache manager removed - was debug feature



  

  const [pricingPlans, setPricingPlans] = useState<any[]>([]);
  const [pricingCurrency, setPricingCurrency] = useState<string>('USD');

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
    // MapLibre GL JS ready - no token needed for OpenStreetMap
  }, []);

  // Clear the geolocation watch when the app unmounts so it doesn't keep
  // firing (and leaking GPS/battery/re-renders) after the user leaves.
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  // Load user language when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserLanguage();
    }
  }, [isAuthenticated, user]);

  // Check disclaimer status when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      checkDisclaimerStatus();
      loadUserPreferences();
      loadPriceRange();
      loadMaxRestaurants();
    }
  }, [isAuthenticated]);


  const checkDisclaimerStatus = async () => {
    if (isAuthenticated) {
      const result = await authService.checkDisclaimerStatus();
      setDisclaimerAccepted(result.hasAccepted);
    } else {
      // For non-authenticated users, check localStorage
      const disclaimerAccepted = localStorage.getItem('disclaimerAccepted') === 'true';
      const doNotShowAgain = localStorage.getItem('disclaimerDoNotShowAgain') === 'true';
      setDisclaimerAccepted(disclaimerAccepted && doNotShowAgain);
    }
  };

  // Load user restaurant preferences
  const loadUserPreferences = async () => {
    if (!isAuthenticated) {
      setUserPreferences(null);
      return;
    }

    try {
      const response = await fetch(buildApiUrl('/api/preferences/restaurants'), {
        credentials: 'include'
      });

      if (response.ok) {
        const preferences = await response.json();
        setUserPreferences(preferences);
        console.log('🍽️ Loaded user restaurant preferences:', preferences);
      } else {
        console.log('🍽️ No restaurant preferences found, using defaults');
        setUserPreferences({
          preferred_chains: [],
          keywords: [],
          search_level: 'include_all'
        });
      }
    } catch (error) {
      console.error('❌ Failed to load user preferences:', error);
      setUserPreferences({
        preferred_chains: [],
        keywords: [],
        search_level: 'include_all'
      });
    }
  };






  const acceptDisclaimer = async (doNotShowAgain: boolean) => {
    const result = await authService.acceptDisclaimer(doNotShowAgain);
    
    if (result.success) {
      setDisclaimerAccepted(doNotShowAgain);
      closeDisclaimerModal();
      handleGeolocate();
    } else {
      console.error('Failed to accept disclaimer:', result.error);
    }
  };



  const loadPricingPlans = async (currency?: string) => {
    const result = await settingsService.loadPricingPlans(currency);
    if (result) {
      setPricingPlans(result.plans);
      setPricingCurrency(result.currency);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    setIsLoadingSubscription(true);
    
    const result = await apiService.handleSubscribe(priceId);
    
    if (!result.success) {
      console.error('Subscription error:', result.error);
    }
    
    setIsLoadingSubscription(false);
  };

  const handleSubscriptionChange = async (priceId: string) => {
    await handleSubscribe(priceId);
    
    // Close the subscription management modal after successful change
    closeSubscriptionManagementModal();
    
    // Refresh subscription info
    fetchUserSubscription();
  };


  // Load settings when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadSettings();
      loadSubscriptionInfo();
      
      // Load subscription and tier data for authenticated users
      console.log('🎯 App.tsx: About to call fetchUserSubscription and fetchAvailableTiers');
      fetchUserSubscription();
      console.log('🎯 App.tsx: About to call fetchAvailableTiers...');
      fetchAvailableTiers();
      console.log('🎯 App.tsx: Called fetchAvailableTiers');
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load pricing plans on mount and when authentication changes
  useEffect(() => {
    loadPricingPlans();
  }, [isAuthenticated]);

  // Ensure pricing plans are loaded when pricing modal opens
  useEffect(() => {
    if (showPricingModal) {
      loadPricingPlans();
    }
  }, [showPricingModal]);

  // Prevent body scroll when modal is open (prevents background page dragging on mobile)
  useEffect(() => {
    if (overlayOpen) {
      document.body.style.overflow = 'hidden';
      // Don't set touchAction on body - let legend handle its own touch events
    } else {
      document.body.style.overflow = '';
    }
    
    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [overlayOpen]);

  // Force mobile viewport constraints for map - ONLY on mobile devices
  useEffect(() => {
    const handleResize = () => {
      // Only apply mobile constraints on actual mobile devices, not just small desktop windows
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isSmallViewport = window.innerWidth <= 768;
      
      if (mapRef.current && isMobileDevice && isSmallViewport) {
        console.log('📱 Mobile device detected, forcing map resize...');
        // Force map to resize to mobile viewport
        mapRef.current.resize();
        
        // Also force the container to exact mobile dimensions
        const mapContainer = document.querySelector('.mobile-map-container');
        if (mapContainer) {
          const container = mapContainer as HTMLElement;
          container.style.width = '100vw';
          container.style.height = selectedCharger ? '50vh' : 'calc(100vh - 40px)';
          container.style.maxWidth = '100vw';
          container.style.maxHeight = selectedCharger ? '50vh' : 'calc(100vh - 40px)';
          container.style.overflow = 'hidden';
          container.style.position = 'absolute';
          container.style.top = '40px';
          container.style.left = '0';
          container.style.right = '0';
          container.style.bottom = '0';
          container.style.zIndex = '1';
          
          // Force all map elements to respect boundaries
          const mapElements = container.querySelectorAll('.maplibregl-map, .maplibregl-canvas-container, .maplibregl-canvas');
          mapElements.forEach((el) => {
            const element = el as HTMLElement;
            element.style.width = '100%';
            element.style.height = '100%';
            element.style.maxWidth = '100vw';
            element.style.maxHeight = selectedCharger ? '50vh' : 'calc(100vh - 40px)';
            element.style.overflow = 'hidden';
          });
          
          console.log('✅ Mobile viewport constraints applied');
        }
      } else if (mapRef.current && !isMobileDevice) {
        // On desktop, ensure map uses normal viewport behavior
        console.log('🖥️ Desktop detected, using normal viewport behavior...');
        mapRef.current.resize();
        
        // Reset any mobile constraints that might have been applied
        const mapContainer = document.querySelector('.mobile-map-container');
        if (mapContainer) {
          const container = mapContainer as HTMLElement;
          container.style.width = '';
          container.style.height = '';
          container.style.maxWidth = '';
          container.style.maxHeight = '';
          container.style.overflow = '';
          container.style.position = '';
          container.style.top = '';
          container.style.left = '';
          container.style.right = '';
          container.style.bottom = '';
          container.style.zIndex = '';
          
          // Reset map elements to normal behavior
          const mapElements = container.querySelectorAll('.maplibregl-map, .maplibregl-canvas-container, .maplibregl-canvas');
          mapElements.forEach((el) => {
            const element = el as HTMLElement;
            element.style.width = '';
            element.style.height = '';
            element.style.maxWidth = '';
            element.style.maxHeight = '';
            element.style.overflow = '';
          });
          
          console.log('✅ Desktop viewport behavior restored');
        }
      }
    };
    
    // Apply on mount
    handleResize();
    
    // Apply on resize
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [mapRef, selectedCharger]);

  const getNetworks = () => getNetworksUtil(setSelectedNetworks);

  const handleGeolocate = async () => {
    console.log('🗺️ handleGeolocate called - starting geolocation process');
    
    // CRITICAL FIX: Reset all previous map state to ensure clean initial state
    console.log('🧹 Resetting previous map state for clean initial state');
    setSelectedCharger(null);
    setNearbyRestaurants([]);
    setChargerAvailability(null);
    setSuggestedMealResults([]); // Clear any suggested meal results from previous mode
    
    // Show loading state immediately
    setOverlayOpen(true);
    setMapLoading(true);
    
    // Start with a default location (Uppsala area) to show superchargers immediately  
    const defaultLat = 59.7683;  // Exactly 10km south of Uppsala center (59.8584)
    const defaultLon = 17.6389;
    
    // ALWAYS set viewport immediately with default location to ensure map shows
    console.log('🗺️ Setting default viewport to ensure map loads');
    setUserLocation({ latitude: defaultLat, longitude: defaultLon, heading: undefined });
    setViewport({
      latitude: defaultLat,
      longitude: defaultLon,
      zoom: 7,
      bearing: 0,
      pitch: 0
    });
    
    // Initialize with empty superchargers - will be populated by API call
    
    // Don't set viewport immediately - wait for real geolocation
    // setViewport will be set in the geolocation success callback
    
    // Don't fetch superchargers for default location - wait for real geolocation or geolocation error
    // fetchSuperchargersForLocation(defaultLat, defaultLon);
    
    // Loading spinner will be hidden when data is actually loaded and map is ready
    // You could add a loading spinner here if you have one
    // Use browser geolocation for all users
    handleBrowserGeolocation();
  };


  // Helper function to calculate if a point is within the blue circle
  const isChargerInCircle = (charger: any, mapCenter: {lat: number, lon: number}, circleRadiusKm: number) => {
    if (!charger.latitude || !charger.longitude) {
      return false;
    }
    
    const distance = calculateDistanceKm(
      mapCenter.lat,
      mapCenter.lon, 
      charger.latitude,
      charger.longitude
    );
    
    return distance <= circleRadiusKm;
  };

  // Helper function to calculate distance between two points (Haversine formula)
  const calculateDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in kilometers
  };

  // Main suggested meal search function
  const performSuggestedMealSearch = async () => {
    console.log('🎯 =================================================');
    console.log('🎯 SUGGESTED MEAL SEARCH DEBUG - STARTING SEARCH');
    console.log('🎯 =================================================');
    
    if (!viewport || !userLocation) {
      console.error('🎯 Cannot perform search: missing viewport or user location');
      console.log('🎯 viewport:', viewport);
      console.log('🎯 userLocation:', userLocation);
      return;
    }

    setSuggestedMealLoading(true);
    setSuggestedMealResults([]);
    
    try {
      console.log('🎯 Starting suggested meal search with preference:', suggestedMealPreference);
      console.log('🎯 Total superchargers available:', superchargers.length);
      
      // Get current map center
      const mapCenter = {
        lat: viewport.latitude,
        lon: viewport.longitude
      };
      console.log('🎯 Map center:', mapCenter);
      
      // Calculate circle radius based on actual visual circle size
      // The blue circle is 288px diameter (144px radius) on screen
      const zoomLevel = viewport.zoom;
      const circlePixelRadius = 72; // More conservative: 144px visual radius = ~72px effective radius
      
      // Use simpler Web Mercator calculation
      // At zoom level Z, 1 pixel = 156543.03392 * cos(latitude) / 2^Z meters
      const metersPerPixel = 156543.03392 * Math.cos(viewport.latitude * Math.PI / 180) / Math.pow(2, zoomLevel);
      const circleRadiusMeters = circlePixelRadius * metersPerPixel;
      const circleRadiusKm = circleRadiusMeters / 1000;
      
      console.log('🎯 Circle radius:', circleRadiusKm, 'km at zoom level', zoomLevel);
      
      // Find chargers within the blue circle
      // First, let's debug the distances for all chargers
      console.log('🎯 DEBUG: Analyzing distances for all chargers...');
      console.log('🎯 DEBUG: Superchargers structure (first 2):', superchargers.slice(0, 2));
      console.log('🎯 DEBUG: First charger keys:', superchargers[0] ? Object.keys(superchargers[0]) : 'No chargers');
      console.log('🎯 DEBUG: First charger AddressInfo keys:', superchargers[0]?.AddressInfo ? Object.keys(superchargers[0].AddressInfo) : 'No AddressInfo');
      
      superchargers.forEach((charger, idx) => {
        if (charger.latitude != null && charger.longitude != null && idx < 5) {
          const distance = calculateDistanceKm(
            mapCenter.lat,
            mapCenter.lon, 
            charger.latitude,
            charger.longitude
          );
          console.log(`🎯 Charger ${charger.id} (${charger.name}): ${distance.toFixed(2)} km from center (within ${circleRadiusKm.toFixed(2)} km? ${distance <= circleRadiusKm})`);
        } else if (idx < 5) {
          console.log(`🎯 Charger ${idx}: Invalid coordinates -`, {
            Lat: charger.latitude,
            Lon: charger.longitude,
            id: charger.id
          });
        }
      });
      
      // Debug the circle radius calculation components
      console.log('🎯 DEBUG: Circle calculation details:');
      console.log('🎯 - Zoom level:', zoomLevel);
      console.log('🎯 - Latitude:', viewport.latitude);
      console.log('🎯 - Circle pixel radius:', circlePixelRadius);
      console.log('🎯 - Meters per pixel:', metersPerPixel);
      console.log('🎯 - Circle radius meters:', circleRadiusMeters);
      console.log('🎯 - Circle radius km:', circleRadiusKm);

      const chargersInCircle = superchargers.filter(charger => 
        isChargerInCircle(charger, mapCenter, circleRadiusKm)
      );
      
      console.log('🎯 Found', chargersInCircle.length, 'chargers within circle');
      console.log('🎯 Chargers in circle:', chargersInCircle.map(c => ({ id: c.ID, title: c.AddressInfo?.Title })));
      
      if (chargersInCircle.length === 0) {
        console.warn('🎯 No chargers found within circle - ending search');
        setSuggestedMealResults([]);
        setSuggestedMealLoading(false);
        return;
      }
      
      // Search for restaurants near the selected chargers
      const allRestaurants: any[] = [];
      let successfulRequests = 0;
      let failedRequests = 0;
      
      console.log('🎯 Starting restaurant search for', chargersInCircle.length, 'chargers...');
      
      for (const charger of chargersInCircle) {
        try {
          console.log(`🎯 Searching restaurants near charger: ${charger.name} (ID: ${charger.id})`);
          
          const restaurants = await searchRestaurantsNearCharger(charger);
          
          console.log(`🎯 API response for charger ${charger.id}:`, restaurants ? restaurants.length : 'null', 'restaurants');
          
          if (restaurants && restaurants.length > 0) {
            successfulRequests++;
            console.log(`🎯 Adding ${restaurants.length} restaurants from charger ${charger.id}`);
            
            // Add charger reference to each restaurant
            restaurants.forEach((restaurant: any) => {
              restaurant.nearCharger = charger;
              if (restaurant.geometry && restaurant.geometry.location) {
                restaurant.distanceToCharger = calculateDistanceKm(
                  charger.latitude,
                  charger.longitude,
                  restaurant.geometry.location.lat,
                  restaurant.geometry.location.lng
                );
              }
            });

            // For suggest a meal, only take top 2 restaurants per charger
            const topRestaurants = restaurants
              .sort((a: any, b: any) => {
                // Sort by favorites first, then rating, then distance
                if (a.isFavorite && !b.isFavorite) return -1;
                if (!a.isFavorite && b.isFavorite) return 1;

                const ratingDiff = (b.rating || 0) - (a.rating || 0);
                if (Math.abs(ratingDiff) > 0.1) return ratingDiff;

                return (a.distanceToCharger || 0) - (b.distanceToCharger || 0);
              })
              .slice(0, 3); // Limit to top 3 restaurants per charger

            console.log(`🎯 Limited to top ${topRestaurants.length} restaurants from charger ${charger.id}`);
            allRestaurants.push(...topRestaurants);
          } else {
            console.warn(`🎯 No restaurants returned for charger ${charger.id}`);
          }
        } catch (error) {
          failedRequests++;
          console.error('🎯 Error searching restaurants near charger:', charger.name, error);
        }
      }
      
      console.log('🎯 Restaurant search summary:');
      console.log('🎯   - Successful requests:', successfulRequests);
      console.log('🎯   - Failed requests:', failedRequests);
      console.log('🎯   - Total restaurants found:', allRestaurants.length);
      console.log('🎯   - Sample restaurants:', allRestaurants.slice(0, 3).map(r => r.name));
      
      // Apply preference filtering
      const filteredRestaurants = applyPreferenceFilter(allRestaurants, suggestedMealPreference);
      
      console.log('🎯 After preference filtering:', filteredRestaurants.length, 'restaurants');
      console.log('🎯 Final results to display:', filteredRestaurants.slice(0, 3).map(r => ({ name: r.name, rating: r.rating })));
      
      setSuggestedMealResults(filteredRestaurants);
      
      console.log('🎯 =================================================');
      console.log('🎯 SUGGESTED MEAL SEARCH DEBUG - COMPLETED');
      console.log('🎯 =================================================');
      
    } catch (error) {
      console.error('🎯 Error in suggested meal search:', error);
    } finally {
      setSuggestedMealLoading(false);
    }
  };

  // Apply preference filtering logic
  const applyPreferenceFilter = (restaurants: any[], preference: 'normal' | 'nicer' | 'cheaper') => {
    if (!restaurants || restaurants.length === 0) return [];
    
    let filtered = [...restaurants];
    
    // Remove duplicates based on place_id
    const uniqueRestaurants = filtered.reduce((acc: any[], current: any) => {
      if (!acc.find(item => item.place_id === current.place_id)) {
        acc.push(current);
      }
      return acc;
    }, []);
    
    switch (preference) {
      case 'nicer':
        // A little nicer: Higher ratings (4.0+) and mid-to-high price levels
        return uniqueRestaurants
          .filter(r => (r.rating || 0) >= 4.0)
          .filter(r => (r.price_level || 1) >= 2) // Price level 2+ (moderate to expensive)
          .sort((a, b) => {
            // Sort by rating descending, then by review count descending
            const ratingDiff = (b.rating || 0) - (a.rating || 0);
            if (ratingDiff !== 0) return ratingDiff;
            return (b.user_ratings_total || 0) - (a.user_ratings_total || 0);
          })
          .slice(0, 20); // Limit to top 20
          
      case 'cheaper':
        // A little cheaper: Lower price levels and good value
        return uniqueRestaurants
          .filter(r => (r.price_level || 1) <= 2) // Price level 1-2 (inexpensive to moderate)
          .filter(r => (r.rating || 0) >= 3.5) // Still maintain decent quality
          .sort((a, b) => {
            // Sort by price level ascending, then by rating descending
            const priceDiff = (a.price_level || 1) - (b.price_level || 1);
            if (priceDiff !== 0) return priceDiff;
            return (b.rating || 0) - (a.rating || 0);
          })
          .slice(0, 20); // Limit to top 20
          
      case 'normal':
      default:
        // Normal preferences: Use existing user preferences
        return uniqueRestaurants
          .filter(r => (r.rating || 0) >= 3.0) // Basic quality filter
          .sort((a, b) => {
            // Sort by distance to charger, then by rating
            const distanceDiff = (a.distanceToCharger || 0) - (b.distanceToCharger || 0);
            if (Math.abs(distanceDiff) > 0.1) return distanceDiff; // If significant distance difference
            return (b.rating || 0) - (a.rating || 0);
          })
          .slice(0, 20); // Limit to top 20
    }
  };

  // Search restaurants near a specific charger using the existing API
  const searchRestaurantsNearCharger = async (charger: any) => {
    if (!charger.latitude || !charger.longitude || !charger.id) {
      console.warn('🎯 Invalid charger data:', charger);
      return [];
    }
    
    const apiUrl = buildApiUrl(`/api/places/superchargers/${charger.id}/restaurants?lat=${charger.latitude}&lng=${charger.longitude}`);
    console.log(`🎯 Making API request to:`, apiUrl);
    
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include' // Include session for authentication
      });
      
      console.log(`🎯 API response status for charger ${charger.id}:`, response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.warn(`🎯 Restaurant search failed for charger ${charger.id}: ${response.status} - ${errorText}`);
        return [];
      }
      
      const data = await response.json();
      console.log(`🎯 API response data for charger ${charger.id}:`, data);
      
      if (data.results && Array.isArray(data.results)) {
        console.log(`🎯 Found ${data.results.length} restaurants for charger ${charger.id}`);
        return data.results;
      } else if (data && Array.isArray(data)) {
        console.log(`🎯 Found ${data.length} restaurants for charger ${charger.id} (direct array)`);
        return data;
      } else {
        console.warn(`🎯 Unexpected response format for charger ${charger.id}:`, data);
        return [];
      }
      
    } catch (error) {
      console.error('🎯 Error searching restaurants near charger:', charger.id, error);
      return [];
    }
  };

  // Helper function to fetch superchargers for a given location with retry logic
  const fetchSuperchargersForLocation = async (lat: number, lon: number, retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = (attempt: number) => Math.min(1000 * Math.pow(2, attempt), 10000); // Exponential backoff, max 10s
    
    // Set loading state only on first attempt
    if (retryCount === 0) {
      setChargersLoading(true);
    }
    
    try {
      console.log(`🗺️ Fetching superchargers for location: ${lat}, ${lon} (attempt ${retryCount + 1}/${maxRetries + 1})`);
      
      // Get selected networks based on user settings
      let networks;
      try {
        networks = await getNetworks();
        console.log('🗺️ Networks fetched:', networks);
      } catch (error) {
        console.warn('🗺️ Failed to fetch networks, using Tesla default:', error);
        networks = [23, 3534, 27]; // Tesla fallback
      }
      
      // Ensure we have valid network IDs
      if (!networks || networks.length === 0) {
        console.warn('🗺️ No networks returned, using Tesla default');
        networks = [23, 3534, 27]; // Tesla fallback
      }
      
      const operatorIds = networks.join(',');
      console.log('🗺️ Operator IDs:', operatorIds);

      // Get user's supercharger radius setting
      let superchargerRadius = 100; // Default
      if (isAuthenticated) {
        superchargerRadius = settings.supercharger_radius_km;
        console.log('🗺️ User supercharger radius setting:', settings.supercharger_radius_km);
      }
      
      // Ensure minimum reasonable radius
      if (superchargerRadius < 10) {
        console.log('🗺️ Radius too small, using minimum of 50km');
        superchargerRadius = 50;
      }
      
      console.log('🗺️ Final supercharger radius:', superchargerRadius);

      // Use secure backend API with longer timeout for car browsers
      const backendUrl = buildApiUrl(`/api/chargers/location?lat=${lat}&lon=${lon}&radius=${superchargerRadius}`);
      console.log('🗺️ Calling secure backend API:', backendUrl);
      
      // Create AbortController with longer timeout for car browsers
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await fetch(backendUrl, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('🗺️ Backend API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Backend API response for default location:', data);
        
        // Data already comes in the correct format from backend, just clean Tesla names
        const transformedData = data.map((item: any) => ({
          ...item,
          name: cleanTeslaName(item.name, item.operatorId)
        }));
        
        setSuperchargers(transformedData);
        setChargersLoading(false); // Hide loading spinner when chargers are loaded
        console.log('🗺️ ✅ Successfully updated with full supercharger list for area:', transformedData.length);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`🗺️ ❌ Error fetching superchargers for default location (attempt ${retryCount + 1}):`, error);
      
      // Retry logic for car browsers with poor connections
      if (retryCount < maxRetries) {
        const delay = retryDelay(retryCount);
        console.log(`🗺️ 🔄 Retrying in ${delay}ms... (attempt ${retryCount + 2}/${maxRetries + 1})`);
        
        setTimeout(() => {
          fetchSuperchargersForLocation(lat, lon, retryCount + 1);
        }, delay);
        return; // Don't clear superchargers yet, retry in progress
      }
      
      // Only clear superchargers after all retries failed, and only if we don't have any
      if (superchargers.length === 0) {
        console.error('🗺️ ❌ All retry attempts failed and no existing chargers to preserve');
        setSuperchargers([]);
      } else {
        console.warn('🗺️ ⚠️ All retry attempts failed but preserving existing chargers on map');
      }
      setChargersLoading(false); // Hide loading spinner even if loading failed
    }
  };

  // Specialized function for suggested meal planning - uses wide radius and ignores user settings
  const fetchSuperchargersForSuggestedMealPlanning = async (latitude: number, longitude: number) => {
    try {
      console.log('🗺️ ===========================================');
      console.log('🗺️ MEAL PLANNING: Fetching superchargers with wide radius via backend');
      console.log('🗺️ Location:', latitude, longitude);
      console.log('🗺️ Will ignore user settings and fetch 300km radius with all networks');
      
      // Use the backend API to avoid CORS issues
      const planningRadius = 300; // Much larger than normal user settings
      const backendUrl = buildApiUrl(`/api/chargers/location?lat=${latitude}&lon=${longitude}&radius=${planningRadius}`);
      
      console.log('🗺️ MEAL PLANNING Backend API URL:', backendUrl);
      console.log('🗺️ Making backend API call...');
      
      const response = await fetch(backendUrl, {
        method: 'GET',
        credentials: 'include'
      });
      console.log('🗺️ Backend API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🗺️ RAW Backend API response data length:', data.length);
        console.log('🗺️ First few items from backend API:', data.slice(0, 3));
        
        // Data should already be in the correct format from backend
        setSuperchargers(data);
        console.log('🗺️ MEAL PLANNING: Updated superchargers state with', data.length, 'chargers');
        console.log('🗺️ ===========================================');
      } else {
        const errorText = await response.text();
        console.error('🗺️ Backend API Error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('🗺️ Error fetching superchargers for meal planning:', error);
      // Don't clear existing superchargers on meal planning failure
      console.warn('🗺️ ⚠️ Meal planning fetch failed but preserving existing chargers on map');
    }
  };

  // Handle charger selection from search modal
  const handleChargerSelection = async (charger: any) => {
    console.log('🔍 Selected charger:', charger);
    
    try {
      // Center map on the selected charger
      setViewport((prevViewport: any) => ({
        latitude: charger.latitude,
        longitude: charger.longitude,
        zoom: 15, // Zoom in to show the area around the charger
        bearing: prevViewport?.bearing || 0,
        pitch: prevViewport?.pitch || 0
      }));

      // Clear existing data
      setSuperchargers([]);
      setNearbyRestaurants([]);
      setSelectedCharger(null);

      // Fetch superchargers in the area (this will include the selected one)
      await fetchSuperchargersForLocation(charger.latitude, charger.longitude);

      // Set the selected charger
      setSelectedCharger(charger);

      // Fetch restaurants near the selected charger
      console.log('🍽️ Fetching restaurants near selected charger...');
      const restaurants = await fetchRestaurantsForCharger(charger);
      setNearbyRestaurants(restaurants);

      // Show the legend with restaurants
      if (restaurants.length > 0) {
        setOverlayOpen(true);
        console.log(`🍽️ Found ${restaurants.length} restaurants near ${charger.name}`);
      } else {
        console.log('🍽️ No restaurants found near the selected charger');
      }

    } catch (error) {
      console.error('🔍 Error handling charger selection:', error);
    }
  };

  const handleBrowserGeolocation = async () => {
    // Check if this is the test user cartest@lagerberget.se
    if (isAuthenticated && user && user.username === 'cartest@lagerberget.se') {
      // Special test mode for cartest@lagerberget.se - simulate northward travel from 10km south of Uppsala
      console.log('🚗 Test mode activated for cartest@lagerberget.se - simulating northward travel');
      
      // Start position: 3km south of Uppsala center (59.8584, 17.6389)
      const startLat = 59.8314;  // 3km south of Uppsala (closer start)
      const startLon = 17.6389;  // Uppsala longitude
      
      // Simulate very high-speed travel (160 km/h = ~0.027 degrees latitude per minute)
      const speedPerSecond = 0.027 / 60; // degrees per second at 160 km/h
      let currentLat = startLat;
      let currentLon = startLon;
      let simulationStartTime = Date.now();
      
      console.log('🚗 Starting fast test simulation at:', currentLat, currentLon, '(3km south of Uppsala)');
      
      // Set initial position
      setUserLocation({ 
        latitude: currentLat, 
        longitude: currentLon, 
        heading: 0 // Heading north 
      });
      
      // Start simulation interval - update every second for faster testing
      const simulationInterval = setInterval(() => {
        const elapsedSeconds = (Date.now() - simulationStartTime) / 1000;
        
        // Move north at 160 km/h
        currentLat = startLat + (speedPerSecond * elapsedSeconds);
        
        
        setUserLocation({ 
          latitude: currentLat, 
          longitude: currentLon, 
          heading: 0 // Heading north
        });
        
        // Stop simulation after 10 minutes (travel ~20km north at 120km/h)
        if (elapsedSeconds > 600) { // 10 minutes
          console.log('🚗 Fast test simulation completed after 10 minutes');
          clearInterval(simulationInterval);
        }
      }, 1000); // Update every second for faster testing
      
      // Store interval reference for cleanup
      (window as any).testSimulationInterval = simulationInterval;
      
      // Set initial viewport for test user
      setViewport({
        latitude: currentLat,
        longitude: currentLon,
        zoom: 7,
        bearing: 0,
        pitch: 0
      });

      // Zoom effect will be triggered after superchargers are loaded

      // Fetch superchargers for test location
      const networks = await getNetworks();
      console.log('🚗 Test mode - Networks fetched:', networks);
      const operatorIds = networks.join(',');
      console.log('🚗 Test mode - Operator IDs:', operatorIds);
      let superchargerRadius = 100; // Default
      if (isAuthenticated) {
        superchargerRadius = settings.supercharger_radius_km;
        console.log('🚗 Test mode - User supercharger radius:', superchargerRadius);
      }

      // Call secure backend API for test location
      try {
        const backendUrl = buildApiUrl(`/api/chargers/location?lat=${currentLat}&lon=${currentLon}&radius=${superchargerRadius}`);
        console.log('🚗 Test mode - Backend API URL:', backendUrl);
        const response = await fetch(backendUrl, {
          method: 'GET',
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('🚗 Test mode - Backend API response count:', data.length);
          console.log('🚗 Test mode - Backend API response:', data);
          
          // Data already comes in correct format from backend, just clean Tesla names
          const transformedData = data.map((item: any) => ({
            ...item,
            name: cleanTeslaName(item.name, item.operatorId),
            // Use numberOfPoints from API response (already in correct format)
            numberOfPoints: item.numberOfPoints || null
          }));
          
          console.log('🚗 Test mode - Transformed supercharger count:', transformedData.length);
          console.log('🚗 Test mode - Transformed supercharger data:', transformedData);
          console.log('🚗 Test mode - About to call setSuperchargers...');
          setSuperchargers(transformedData);
          console.log('🚗 Test mode - setSuperchargers called successfully');
          
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (err) {
        console.error('🚗 Test mode - Error fetching superchargers from OpenChargeMap:', err);
        setSuperchargers([]);
      }

      setOverlayOpen(true);
      
    } else {
      // Use real GPS coordinates for regular users
      if (navigator.geolocation) {
        // Starting geolocation
        
        // Add timeout and error handling - no caching for accurate location
        const geolocationOptions = {
          enableHighAccuracy: true,
          timeout: 15000, // 15 second timeout for better accuracy
          maximumAge: 0 // No caching - always get fresh location
        };
        
        try {
          geolocateWithRetry(async (position) => {
            const lat = position.coords.latitude;
            const lon = position.coords.longitude;
            const heading = position.coords.heading;

          // Set user location FIRST, before any API calls that might fail
          console.log('🌍 NORMAL: Setting real geolocation to:', lat, lon);
          setUserLocation({ 
            latitude: lat, 
            longitude: lon, 
            heading: heading !== null ? heading : undefined 
          });
          
          // Start watching position for continuous heading updates.
          // Clear any previous watch first so we never leak multiple watchers.
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
          }
          watchIdRef.current = navigator.geolocation.watchPosition(
            (position) => {
              const newLat = position.coords.latitude;
              const newLon = position.coords.longitude;
              const newHeading = position.coords.heading;
              
              console.log('🌍 WATCH: Setting watch position to:', newLat, newLon);
              setUserLocation({ 
                latitude: newLat, 
                longitude: newLon, 
                heading: newHeading !== null ? newHeading : undefined 
              });
            },
            (error) => {
              console.log('Watch position error:', error);
            },
            {
              enableHighAccuracy: true,
              maximumAge: 5000,
              timeout: 10000
            }
          );
          
          // watchId is stored in watchIdRef and cleared on unmount (see effect below).
        setViewport({
          latitude: lat,
          longitude: lon,
          zoom: 7,
          bearing: 0,
          pitch: 0
        });

        // Map centering and zoom effect will happen AFTER data loads and spinner stops
        console.log('🗺️ Geolocation successful - will center map after data loads:', lat, lon);

        // Get selected networks based on user settings
        let networks;
        try {
          networks = await getNetworks();
          console.log('🗺️ Browser geolocation - Networks fetched:', networks);
        } catch (error) {
          console.warn('🗺️ Browser geolocation - Failed to fetch networks, using Tesla default:', error);
          networks = [23, 3534, 27]; // Tesla fallback
        }
        
        // Ensure we have valid network IDs
        if (!networks || networks.length === 0) {
          console.warn('🗺️ Browser geolocation - No networks returned, using Tesla default');
          networks = [23, 3534, 27]; // Tesla fallback
        }
        
        const operatorIds = networks.join(',');
        console.log('🗺️ Browser geolocation - Operator IDs:', operatorIds);

        // Get user's supercharger radius setting
        let superchargerRadius = 100; // Default
        if (isAuthenticated) {
          superchargerRadius = settings.supercharger_radius_km;
        }

        // Call secure backend API instead of OpenChargeMap directly
        try {
          const backendUrl = buildApiUrl(`/api/chargers/location?lat=${lat}&lon=${lon}&radius=${superchargerRadius}`);
          const response = await fetch(backendUrl, {
            method: 'GET',
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Backend API response:', data);
            
            // Data already comes in correct format from backend, just clean Tesla names
            const transformedData = data.map((item: any) => ({
              ...item,
              name: cleanTeslaName(item.name, item.operatorId),
              // Use numberOfPoints from API response (already in correct format)
              numberOfPoints: item.numberOfPoints || null
            }));
            
            console.log('Transformed supercharger data:', transformedData);
            setSuperchargers(transformedData);

            console.log('🗺️ After setting superchargers state - function continues');

            // Wait for map to finish rendering all markers, THEN do zoom + center effect
            if (mapRef.current && transformedData.length > 0) {
              console.log('🗺️ Map data loaded, waiting for markers to render...');

              // Single requestAnimationFrame for smooth timing - avoid double delay
              requestAnimationFrame(() => {
                if (mapRef.current) {
                  console.log('🗺️ All markers rendered, now doing smooth zoom IN + center on user position');

                  const currentZoom = mapRef.current.getZoom();
                  const newZoom = currentZoom + 1; // Zoom IN by 1 step

                  console.log('🗺️ Smooth zoom IN + center:', { userLat: lat, userLon: lon, currentZoom, newZoom });

                  // Hide spinner now that everything is ready
                  setChargersLoading(false);
                  setMapLoading(false);

                  // Ultra-smooth zoom IN + center animation with easing
                  mapRef.current.flyTo({
                    center: [lon, lat], // Center on USER position
                    zoom: newZoom, // Zoom IN by 1 step
                    duration: 1200, // Longer for smoother animation
                    easing: (t: number) => t * (2 - t) // Smooth ease-out curve
                  });
                }
              });
            } else {
              // Fallback if no markers
              setChargersLoading(false);
              setMapLoading(false);
              console.log('🗺️ Zoom effect skipped - missing requirements:', { mapRef: !!mapRef.current, dataLength: transformedData.length });
            }

            // Map cache functionality removed - was debug feature
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } catch (err) {
          console.error('Error fetching superchargers from OpenChargeMap:', err);
          setSuperchargers([]);
        }

        // User location and viewport already set above - no duplication needed
        console.log('🗺️ Setting overlayOpen to true for browser geolocation');
        setOverlayOpen(true);
      }, 
      // Error callback
      (error) => {
        console.error('🗺️ Geolocation error:', error);
        let errorMessage = t('app_geoUnableRetrieve');

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('app_geoPermissionDenied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('app_geoUnavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('app_geoTimeout');
            break;
          default:
            errorMessage = `${t('app_geoErrorLabel')}: ${error.message}`;
        }

        alert(`⚠️ ${t('app_geoErrorTitle')}: ${errorMessage}\n\n${t('app_geoDefaultLocationNote')}`);
        
        // Set a default location (Uppsala area) so the map still works
        const defaultLat = 59.7683; // 10km south of Uppsala center
        const defaultLon = 17.6389; // Uppsala area
        console.log('🚨 FALLBACK: Setting Uppsala area fallback location to:', defaultLat, defaultLon);
        setUserLocation({ latitude: defaultLat, longitude: defaultLon, heading: undefined });
        setViewport({
          latitude: defaultLat,
          longitude: defaultLon,
          zoom: 7,
          bearing: 0,
          pitch: 0
        });

        // Zoom effect will be triggered after superchargers are loaded

        // Still try to fetch superchargers for the default location
        try {
          fetchSuperchargersForLocation(defaultLat, defaultLon);
        } catch (fetchError) {
          console.error('🗺️ Failed to fetch superchargers for default location:', fetchError);
        }
      },
      // Options
      geolocationOptions
    );
        } catch (error) {
          console.error('🗺️ Error in handleBrowserGeolocation:', error);
          alert(t('app_geoFailedGetLocation'));
        }
      } else {
        alert(t('app_geoNotSupported'));
      }
    }
  };


  const handleSuperchargerClick = async (supercharger: any) => {
    try {
      // If in suggested meal mode, exit it when clicking a charger
      if (suggestedMealMode) {
        console.log('🍽️ Exiting suggested meal mode due to charger click');
        exitSuggestedMealMode();
      }

      console.log('Fetching restaurants for supercharger:', supercharger);

      // Use numberOfPoints directly from charger data - no endpoint call needed
      console.log('🔧 DEBUG: Supercharger object received:', supercharger);
      console.log('🔧 DEBUG: supercharger.numberOfPoints:', supercharger.numberOfPoints);
      console.log('🔧 DEBUG: typeof supercharger.numberOfPoints:', typeof supercharger.numberOfPoints);

      const availability = {
        total: supercharger.numberOfPoints || null,
        available: null,
        lastUpdated: new Date().toISOString()
      };
      console.log(`🔋 Using numberOfPoints from charger data: ${availability.total} stalls`);
      
      // Get user's tier limits for restaurants
      const tierLimits = userSubscription ? {
        maxRestaurants: userSubscription.tier === 'free' ? 3 : userSubscription.tier === 'basic' ? 10 : userSubscription.tier === 'bronze' ? 10 : userSubscription.tier === 'silver' ? 25 : -1,
        maxSearchRadius: userSubscription.tier === 'free' ? 1.0 : userSubscription.tier === 'basic' ? 2.0 : userSubscription.tier === 'bronze' ? 2.0 : userSubscription.tier === 'silver' ? 5.0 : 10.0
      } : { maxRestaurants: 3, maxSearchRadius: 200.0 }; // Default to Bronze limits if no subscription info

      const apiUrl = buildApiUrl(`api/places/superchargers/${supercharger.id}/restaurants?lat=${supercharger.latitude}&lng=${supercharger.longitude}&maxResults=${tierLimits.maxRestaurants > 0 ? tierLimits.maxRestaurants : 100}&radius=${tierLimits.maxSearchRadius}`);
      console.log('🔄 Making restaurant API call:', apiUrl);
      const apiStartTime = performance.now();
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'  // Use cookie-based authentication instead of JWT
      });
      const apiEndTime = performance.now();
      const responseTime = Math.round(apiEndTime - apiStartTime);
      console.log(`⏱️ Restaurant API response time: ${responseTime}ms (${responseTime < 500 ? 'likely cached' : 'likely fresh API call'})`);

      if (!response.ok) {
        // Try to get the error details from the response
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = errorData.details || errorData.message || '';
        } catch (e) {
          // If we can't parse the error response, use the status text
          errorDetails = response.statusText;
        }
        
        // Create a more informative error message
        const errorMessage = response.status === 503 
          ? `Restaurant search temporarily unavailable. ${errorDetails}`
          : `HTTP error! status: ${response.status}. ${errorDetails}`;
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Restaurants found:', data);

      // Set availability data from charger directly
      console.log('🔧 Setting chargerAvailability to:', availability);
      setChargerAvailability(availability);
      
      // Check if user is authenticated
      if (data.count !== undefined) {
        // Not authenticated - show count only
        setNearbyRestaurants([]);
        setSelectedCharger({ ...supercharger, restaurantCount: data.count });
        

      } else {
        // Authenticated - show full restaurant data, filtered and sorted by rating (best first)
        let filteredAndSortedRestaurants = data
          .filter((restaurant: any) => restaurant.name) // Only filter out restaurants without names
          .sort((a: any, b: any) => {
            const ratingA = a.rating || 0;
            const ratingB = b.rating || 0;
            return ratingB - ratingA; // Descending order (best rating first)
          });

        // Apply tier-based result limits on the frontend as well (backup to server-side limits)
        if (tierLimits.maxRestaurants > 0) {
          filteredAndSortedRestaurants = filteredAndSortedRestaurants.slice(0, tierLimits.maxRestaurants);
        }

        setNearbyRestaurants(filteredAndSortedRestaurants);
        setSelectedCharger(supercharger);
        
        // Return the restaurants for zoom calculation
        return filteredAndSortedRestaurants;
      }
      
      // Return empty array if no restaurants
      return [];
      
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Set error state to show in the UI
      setNearbyRestaurants([]);
      setSelectedCharger({ 
        ...supercharger, 
        restaurantCount: 0,
        error: errorMessage 
      });
      
      // Also show an alert for immediate feedback
      if (errorMessage.includes('temporarily unavailable')) {
        alert(`🍽️ ${errorMessage}\n\n${t('app_tryAgainFewMinutes')}`);
      } else {
        alert(`🍽️ ${t('app_errorLabel')}: ${errorMessage}`);
      }
      
      // Return empty array on error
      return [];
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Centered crossfade hero background */}
      <div className="absolute inset-0 w-full h-[500px]">
        {/* Left: Restaurant with fade out to center */}
        <img 
          src={RESTAURANT_IMG} 
          alt="Restaurant" 
          className="absolute top-0 left-0 w-full h-full object-cover"
          style={{
            zIndex: 1,
            maskImage: 'linear-gradient(to right, black 48%, transparent 52%)',
            WebkitMaskImage: 'linear-gradient(to right, black 48%, transparent 52%)',
          }}
        />
        {/* Right: Map with fade in from center */}
        <>
          <div
            className="absolute top-0 left-0 w-full h-full md:hidden"
            style={{
              zIndex: 2,
              maskImage: 'linear-gradient(to right, transparent 46%, black 54%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 46%, black 54%)',
              backgroundImage: `url(${MAP_IMG})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '190%',
              backgroundPosition: '40% 20%',
            }}
          />
          <div
            className="absolute top-0 left-0 w-full h-full hidden md:block"
            style={{
              zIndex: 2,
              maskImage: 'linear-gradient(to right, transparent 48%, black 52%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 48%, black 52%)',
              backgroundImage: `url(${MAP_IMG})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: '150% 20%',
            }}
          />
        </>
        {/* Full-width overlay for text readability, only over hero */}
        <div className="absolute inset-0 w-full h-full bg-black bg-opacity-40" style={{zIndex: 3}}></div>
      </div>

      <Navigation
        t={t}
        language={language}
        isAuthenticated={isAuthenticated}
        user={user}
        userSubscription={userSubscription}
        mapCacheStatus={{ isActive: false, cachedTiles: 0, estimatedSavings: 0 }}
        mobileMenuOpen={mobileMenuOpen}
        onLanguageChange={(lang) => {
          setLanguage(lang);
          saveUserLanguage(lang);
          setGlobalLanguage(lang); // keep all useTranslations() components in sync
        }}
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        onShowContactModal={openContactModal}
        onShowFAQModal={openFAQModal}
        onShowAboutModal={openAboutModal}
        onShowPricingModal={openPricingModal}
        onShowSettingsModal={openSettingsModal}
        onShowLoginModal={openLoginModal}
        onShowRegisterModal={openRegisterModal}
        onShowSubscriptionModal={openSubscriptionManagementModal}
        onLogout={handleLogout}
      />

      {/* About Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg p-8 w-full max-w-lg mx-4 relative">
            <button
              className="absolute top-3 right-3 bg-gray-200 hover:bg-gray-300 rounded-full px-4 py-2 text-2xl font-bold z-10"
              onClick={() => closeAboutModal()}
              aria-label={t('app_close')}
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-4 text-center">{t('app_aboutWhere2')}</h2>
            <p className="text-gray-700 text-base leading-relaxed">
              {t('app_aboutText')}
            </p>
          </div>
        </div>
      )}

      {/* Hero Section with overlay for readability */}
      <div className="relative z-10 flex items-center justify-center" style={{height: 500}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-full w-full justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center w-full relative"
            style={{zIndex: 4}}
          >
            {/* Personalized greeting for authenticated users */}
            {isAuthenticated && user ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-4"
              >
                <p className="text-xl text-white font-semibold drop-shadow-xl" style={{textShadow: '0 2px 16px rgba(0,0,0,0.7)'}}>
                  {t('app_welcomeBack')}, {user.displayName ? user.displayName.split(' ')[0] : user.username}!
                </p>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-4"
              >
                <p className="text-lg text-white font-medium drop-shadow-xl" style={{textShadow: '0 2px 16px rgba(0,0,0,0.7)'}}>
                  <EmojiGif emoji="🚗" size="sm" className="mr-2" />
                  {t('app_readyToFindFood')}
                </p>
              </motion.div>
            )}
            
            <h1 className="text-3xl tracking-tight font-extrabold text-white sm:text-4xl md:text-5xl drop-shadow-xl" style={{textShadow: '0 2px 16px rgba(0,0,0,0.7)'}}>
              <span className="block">{t('heroTitle')}</span>
            </h1>
            
            {/* Conditional content based on authentication and subscription */}
            {isAuthenticated ? (
              // Protected landing page for subscribers - minimal content now that news moved to bottom-right
              <div className="mt-6">
                <p className="text-lg text-white/80 text-center max-w-2xl mx-auto">
                  {t('app_welcomeBackReady')}
                </p>
              </div>
            ) : (
              // Public landing page for non-subscribers
              <>
                <p className="mt-3 max-w-xl mx-auto text-base text-gray-200 sm:text-lg md:mt-5 md:text-lg md:max-w-2xl drop-shadow-xl" style={{textShadow: '0 2px 16px rgba(0,0,0,0.7)'}}>
                  {t('heroSubtitle')}
                </p>
                <div className="mt-4 max-w-md mx-auto sm:flex sm:justify-center md:mt-6">
                  <div className="rounded-md shadow">
                    <button 
                      onClick={handleGeolocate}
                      className="w-full flex items-center justify-center px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:py-3 md:text-lg md:px-8"
                    >
                      {t('getStarted')}
                    </button>
                  </div>
                  <div className="mt-2 rounded-md shadow sm:mt-0 sm:ml-3">
                    <button 
                      onClick={() => openLearnMoreModal()}
                      className="w-full flex items-center justify-center px-6 py-2 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50 md:py-3 md:text-lg md:px-8"
                    >
                      {t('learnMore')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>


      {/* Features Section with images - three columns, compact */}
      <div className="relative z-10 bg-white py-2" id="features">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:items-stretch">
            {/* Feature 1 - Restaurant Discovery (swapped with Smart Location Search) */}
            <div className="flex flex-col items-center text-center p-2 h-full">
              <img src={FEATURE_IMAGES[0].src} alt={FEATURE_IMAGES[0].alt} className="rounded-xl shadow-lg w-full h-28 object-cover mb-2 bg-white" />
              <h3 className="text-lg font-bold text-gray-900 mb-1">{t('restaurantDiscovery')}</h3>
              <p className="text-xs text-gray-500 mb-6 flex-grow">
                {t('restaurantDesc')}
              </p>
              {/* Find nearby restaurants button - moved below Restaurant Discovery text */}
              <div className="mt-auto">
                <button
                  onClick={() => {
                    if (isAuthenticated && disclaimerAccepted) {
                      // Make sure suggested meal mode is OFF when using "Find a restaurant"
                      exitSuggestedMealMode();
                      handleGeolocate();
                    } else {
                      openDisclaimerModal();
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded-full shadow-lg text-sm transition-all duration-200"
                >
                  {t('findNearbyRestaurants')}
                </button>
              </div>
            </div>
            {/* Feature 2 - Pre-Order & Ready (stays in middle) */}
            <div className="flex flex-col items-center text-center p-2 h-full">
              <img src={FEATURE_IMAGES[1].src} alt={FEATURE_IMAGES[1].alt} className="rounded-xl shadow-lg w-full h-28 object-cover mb-2 bg-white" />
              <h3 className="text-lg font-bold text-gray-900 mb-1">{t('preOrderReady')}</h3>
              <p className="text-xs text-gray-500 mb-6 flex-grow">
                {t('preOrderDesc')}
              </p>
              {/* Get suggestions button - added below the Meal Suggestions title */}
              <div className="mt-auto">
                <button
                  onClick={() => {
                    if (!isAuthenticated || !disclaimerAccepted) {
                      openDisclaimerModal();
                    } else {
                      // Check if user has premium access (Premium tier or Premium trial)
                      const hasPremiumAccess = userSubscription && (
                        userSubscription.tier === 'plus' ||
                        (userSubscription.isTrial && userSubscription.tier === 'plus')
                      );

                      if (hasPremiumAccess) {
                        // Open the suggested meal modal
                        openSuggestedMealModal();
                      } else {
                        // Show pricing modal for upgrade
                        openPricingModal();
                      }
                    }
                  }}
                  className={`font-bold py-2 px-4 rounded-full shadow-lg text-sm transition-all duration-200 flex items-center justify-center gap-1 ${
                    isAuthenticated && userSubscription && !(
                      userSubscription.tier === 'plus' ||
                      (userSubscription.isTrial && userSubscription.tier === 'plus')
                    )
                      ? 'bg-gray-400 text-gray-700 hover:bg-gray-500'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                  title={
                    isAuthenticated && userSubscription && !(
                      userSubscription.tier === 'plus' ||
                      (userSubscription.isTrial && userSubscription.tier === 'plus')
                    )
                      ? t('app_plusFeatureUpgrade')
                      : ''
                  }
                >
                  {isAuthenticated && userSubscription && !(
                    userSubscription.tier === 'plus' ||
                    (userSubscription.isTrial && userSubscription.tier === 'plus')
                  ) && (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                    </svg>
                  )}
                  {t('app_getSuggestions')}
                </button>
              </div>
            </div>
            {/* Feature 3 - Smart Location Search (swapped with Restaurant Discovery) */}
            <div className="flex flex-col items-center text-center p-2 h-full">
              <img src={FEATURE_IMAGES[2].src} alt={FEATURE_IMAGES[2].alt} className="rounded-xl shadow-lg w-full h-28 object-cover mb-2 bg-white" />
              <h3 className="text-lg font-bold text-gray-900 mb-1">{t('smartLocationSearch')}</h3>
              <p className="text-xs text-gray-500 mb-6 flex-grow">
                {t('smartLocationDesc')}
              </p>
              {/* Search Chargers button */}
              <div className="mt-auto">
                <button
                  onClick={() => {
                    if (isAuthenticated && disclaimerAccepted) {
                      setChargerSearchModalOpen(true);
                    } else {
                      openDisclaimerModal();
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded-full shadow-lg text-sm transition-all duration-200"
                >
                  {t('searchChargers')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative z-10 bg-primary-700">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:py-10 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              <span className="block">{t('ctaTitle')}</span>
              <span className="block text-primary-200">{t('ctaSubtitle')}</span>
            </h2>
          </div>
        </div>
      </div>




      {/* Overlay Modal - Responsive for Mobile */}
      {overlayOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4 md:p-6"
          onTouchMove={(e) => {
            // Only prevent dragging if touching the backdrop itself, not child elements
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onTouchStart={(e) => {
            // Only prevent touch events on modal backdrop itself
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div className="relative bg-white rounded-lg shadow-lg w-full h-full max-w-[calc(100vw-1rem)] max-h-[calc(100vh-1rem)] sm:max-w-[calc(100vw-2rem)] sm:max-h-[calc(100vh-2rem)] md:max-w-[95vw] md:max-h-[95vh] lg:max-w-[90vw] lg:max-h-[90vh] xl:max-w-[85vw] xl:max-h-[85vh] flex flex-col overflow-hidden">
            <div className="absolute top-2 right-2 md:top-3 md:right-3 flex space-x-2 z-10">
              {/* Back to supercharger view button - only show when viewing restaurants */}
              {selectedCharger && nearbyRestaurants.length > 0 && (
                <button
                  onClick={() => { 
                    // Center on user location like the zoom minus button does
                    if (mapRef.current && userLocation) {
                      mapRef.current.flyTo({
                        center: [userLocation.longitude, userLocation.latitude],
                        zoom: 7, // Same zoom as initial supercharger view
                        duration: 500
                      });
                    }
                    setSelectedCharger(null); 
                    setNearbyRestaurants([]); 
                    setChargerAvailability(null);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold text-xl md:text-lg w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center"
                  title={t('app_backToSuperchargerView')}
                >
                  ←
                </button>
              )}
              {/* Close map button */}
              <button
                className="bg-gray-200 hover:bg-gray-300 rounded-full px-2 py-1 md:px-3 md:py-1 text-xl md:text-lg font-bold"
                onClick={() => { setOverlayOpen(false); setSelectedCharger(null); setNearbyRestaurants([]); }}
                title={t('app_closeMap')}
              >
                ×
              </button>
            </div>
            {/* Loading spinner overlay */}
            {(mapLoading || chargersLoading) && (
              <div className="absolute inset-0 bg-white bg-opacity-90 z-20 flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-lg text-gray-700 font-medium">
                    {mapLoading ? t('app_loadingMap') : t('app_loadingChargers')}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    {mapLoading ? t('app_findingSuperchargers') : t('app_fetchingStations')}
                  </p>
                  {chargersLoading && (
                    <p className="text-xs text-gray-400 mt-1">
                      {t('app_slowConnectionNote')}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {viewport && (
              <div className="relative w-full h-full">

                {/* New Floating Legend with proper positioning */}
                <FloatingLegend
                  ref={restaurantListRef}
                  isVisible={selectedCharger !== null}
                  isAuthenticated={isAuthenticated}
                  onClose={() => {
                    // Center on user location like the zoom minus button does
                    if (mapRef.current && userLocation) {
                      mapRef.current.flyTo({
                        center: [userLocation.longitude, userLocation.latitude],
                        zoom: 7, // Same zoom as initial supercharger view
                        duration: 500
                      });
                    }
                    setSelectedCharger(null);
                    setNearbyRestaurants([]);
                    setChargerAvailability(null);
                  }}
                  onUpgradePrompt={() => {
                    // Open login modal for upgrade
                    openLoginModal();
                  }}
                  nearbyRestaurants={nearbyRestaurants}
                  selectedCharger={selectedCharger}
                  chargerAvailability={chargerAvailability}
                  onRestaurantClick={(restaurant) => {
                    // Restaurant clicked from legend - the selectedRestaurantIndex is already set by MapLegend
                    // No need to set it again here, just handle any additional logic if needed
                    console.log('🍽️ onRestaurantClick called for:', restaurant.name, '(state already set by MapLegend)');
                  }}
                  selectedRestaurantIndex={selectedRestaurantIndex}
                  setSelectedRestaurantIndex={setSelectedRestaurantIndex}
                  onReportNonRestaurant={handleReportNonRestaurant}
                  userPreferences={userPreferences}
                  t={t}
                />

                {/* Map Container - Full Width */}
                <div className="w-full h-full" style={{ minHeight: '400px', height: '100%' }}>
                  {mapboxToken ? (
                    <div className="relative w-full h-full">
                      <MapErrorBoundary>
                      <MapLibreMap
                        ref={mapRef}
                        initialViewState={viewport}
                        style={{ width: '100%', height: '100%', borderRadius: '0.5rem' }}
                        onMove={(evt: any) => setViewport(evt.viewState)}
                      >
                {/* User location marker */}
                {userLocation && (
                  <MapLibreMarker
                    longitude={userLocation.longitude}
                    latitude={userLocation.latitude}
                    anchor="center"
                  >
                    <DirectionalArrow 
                      heading={userLocation.heading}
                    />
                  </MapLibreMarker>
                )}
                
                {/* Restaurant markers with letters - render BEFORE superchargers so they appear BEHIND */}
                {selectedCharger && (() => {
                  // Reorder restaurants so selected one comes last (highest z-index)
                  const reorderedRestaurants = [...nearbyRestaurants];
                  if (selectedRestaurantIndex !== null && selectedRestaurantIndex >= 0) {
                    const selectedRestaurant = reorderedRestaurants.splice(selectedRestaurantIndex, 1)[0];
                    reorderedRestaurants.push(selectedRestaurant);
                  }
                  
                  return reorderedRestaurants
                    .filter(restaurant => restaurant && restaurant.longitude != null && restaurant.latitude != null)
                    .map((restaurant, displayIdx) => {
                    const originalIdx = nearbyRestaurants.indexOf(restaurant);
                    const letter = String.fromCharCode(65 + originalIdx); // A, B, C, etc.
                    return (
                      <MapLibreMarker
                        key={`restaurant-${originalIdx}`}
                        longitude={restaurant.longitude}
                        latitude={restaurant.latitude}
                        anchor="bottom"
                        onClick={() => {
                          // Use originalIdx directly - no need to findIndex since we already have it
                          setSelectedRestaurantIndex(originalIdx);
                          
                          // Scroll the corresponding restaurant card into view
                          if (restaurantListRef.current) {
                            setTimeout(() => {
                              const restaurantCards = restaurantListRef.current?.querySelectorAll('.restaurant-card');
                              if (restaurantCards && restaurantCards[originalIdx]) {
                                restaurantCards[originalIdx].scrollIntoView({ 
                                  behavior: 'smooth', 
                                  block: 'start',
                                  inline: 'nearest'
                                });
                              }
                            }, 100);
                          }
                        }}
                      >
                        <div 
                          className={`${selectedRestaurantIndex === originalIdx 
                            ? 'w-8 h-8 bg-blue-600 text-white shadow-xl border-4 border-yellow-400 ring-4 ring-yellow-200' 
                            : 'w-6 h-6 bg-blue-500 text-white border-2 border-white'
                          } rounded-full flex items-center justify-center text-xs font-bold shadow-lg cursor-pointer hover:bg-blue-600 transition-all`}
                          title={`${restaurant.name} (${restaurant.distance_km ? restaurant.distance_km.toFixed(1) + 'km' : '20m'})`}
                          style={{ zIndex: 10 }} // Restaurant markers at z-index 10
                        >
                          {letter}
                        </div>
                      </MapLibreMarker>
                    );
                  });
                })()}

                {/* Supercharger markers - render AFTER restaurants so they appear ON TOP */}
                {superchargers.map((station, i) => (
                  <MapLibreMarker
                    key={station.id}
                    longitude={station.longitude}
                    latitude={station.latitude}
                    anchor="bottom"
                    onClick={async (e) => {
                      // Supercharger clicked via marker
                      
                      // First, save the current viewport BEFORE moving the map
                      if (mapRef.current) {
                        const currentCenter = mapRef.current.getCenter();
                        const currentZoom = mapRef.current.getZoom();
                        
                        const viewportToSave = {
                          longitude: currentCenter.lng,
                          latitude: currentCenter.lat,
                          zoom: currentZoom
                        };
                        
                        // Saving previous viewport
                        setPreviousViewport(viewportToSave);
                      }
                      
                      // Start zoom immediately with default zoom, fetch restaurants in parallel
                      console.log('🎯 Setting selectedCharger:', station.name);
                      console.log('🎯 Full charger data:', station);
                      setSelectedCharger(station);
                      
                      // Immediate zoom to supercharger (no offset needed - legend will float)
                      if (mapRef.current) {
                        mapRef.current.flyTo({
                          center: [station.longitude, station.latitude],
                          zoom: 13,
                          duration: 600
                        });
                      }
                      
                      // Fetch restaurants in parallel (non-blocking)
                      handleSuperchargerClick(station).then(restaurants => {
                        setNearbyRestaurants(restaurants || []);
                        
                        // Only adjust zoom if significantly different from current zoom AND zooming IN
                        if (mapRef.current && restaurants && restaurants.length > 0) {
                          const optimalZoom = calculateOptimalZoom(station, restaurants);
                          const currentZoom = mapRef.current.getZoom();
                          const zoomDifference = optimalZoom - currentZoom;
                          
                          // Only adjust if zoom difference is significant (more than 1 level) AND zooming IN
                          if (zoomDifference > 1) {
                            mapRef.current.flyTo({
                              center: [station.longitude, station.latitude],
                              zoom: optimalZoom,
                              duration: 400
                            });
                          }
                        }
                      });
                    }}
                  >
                    {(() => {
                      // Determine network type based on operator ID and name
                      const isTesla = station.operatorId === 23 || station.operatorId === 3534 || station.operatorId === 27 ||
                                     station.name?.toLowerCase().includes('tesla') || 
                                     station.name?.toLowerCase().includes('supercharger');
                      const isIonity = station.operatorId === 3299 || station.name?.toLowerCase().includes('ionity');
                      const isOKQ8 = station.operatorId === 3763 || station.name?.toLowerCase().includes('okq8');
                      
                      // Check if station has preferred restaurants (placeholder - will be implemented with restaurant preferences)
                      const hasPreferredRestaurants = favoriteChargerIds.has(station.id); // heart + gold outline when a favourite restaurant is nearby
                      
                      // Network-specific colors and sizes
                      let pinColor, strokeColor, markerSize, goldOutline;
                      
                      // Gold outline for stations with preferred restaurants
                      goldOutline = hasPreferredRestaurants ? '#fbbf24' : null; // Gold color
                      
                      if (isTesla) {
                        pinColor = '#dc2626'; // Red
                        strokeColor = goldOutline || '#b91c1c'; // Gold outline or darker red
                        markerSize = 'w-12 h-12'; // 48px
                      } else if (isIonity) {
                        pinColor = '#1e3a8a'; // Dark blue  
                        strokeColor = goldOutline || '#1e40af'; // Gold outline or slightly different blue
                        markerSize = 'w-12 h-12'; // 48px
                      } else if (isOKQ8) {
                        pinColor = '#eab308'; // Yellow
                        strokeColor = goldOutline || '#ca8a04'; // Gold outline or darker yellow
                        markerSize = 'w-12 h-12'; // 48px
                      } else {
                        pinColor = '#6b7280'; // Gray
                        strokeColor = goldOutline || '#4b5563'; // Gold outline or darker gray
                        markerSize = 'w-10 h-10'; // 40px for unknown networks
                      }
                      
                      return (
                        <div
                          className={`${markerSize} shadow-lg cursor-pointer`}
                          title={hasPreferredRestaurants ? t('map_favoriteChargerTooltip') : station.name}
                          style={{ position: 'relative', pointerEvents: 'auto', zIndex: 20 }} // Charger markers at z-index 20 (higher than restaurants)
                        >
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            className="drop-shadow-lg"
                          >
                            {/* Map pin background */}
                            <path
                              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                              fill={pinColor}
                              stroke={strokeColor}
                              strokeWidth={hasPreferredRestaurants ? "2.5" : "1"}
                            />
                            {/* White lightning bolt inside - extra large */}
                            <path
                              d="M12.5 6.5l-2 3.5h2.5l-2 5 4-6h-2.5z"
                              fill="white"
                              stroke="white"
                              strokeWidth="0.5"
                            />
                          </svg>
                          {/* Heart badge when this charger has a favourite restaurant nearby */}
                          {hasPreferredRestaurants && (
                            <span
                              aria-label={t('map_favoriteChargerTooltip')}
                              style={{
                                position: 'absolute',
                                top: '-3px',
                                right: '-3px',
                                fontSize: '15px',
                                lineHeight: 1,
                                filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.45))',
                                pointerEvents: 'none',
                              }}
                            >
                              ❤️
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </MapLibreMarker>
                ))}
                


                      </MapLibreMap>
                      </MapErrorBoundary>

                      {/* Simple Blue Circle - only when in suggested meal mode */}
                      {suggestedMealMode && (
                        <div 
                          className="absolute inset-0 pointer-events-none flex items-center justify-center"
                        >
                          <div 
                            className="w-72 h-72 rounded-full bg-blue-500 opacity-30 border-2 border-blue-600"
                          />
                        </div>
                      )}
                      
                      {/* Custom Zoom Controls that always center on user location */}
                      {userLocation && (
                        <div className="absolute top-4 left-4 z-10 flex flex-col space-y-1">
                          <button
                            onClick={() => {
                              if (mapRef.current && userLocation) {
                                const currentZoom = mapRef.current.getZoom();
                                mapRef.current.flyTo({
                                  center: [userLocation.longitude, userLocation.latitude],
                                  zoom: Math.min(currentZoom + 1, 18), // Max zoom 18
                                  duration: 500
                                });
                              }
                            }}
                            className="w-8 h-8 bg-white border border-gray-300 shadow-md hover:bg-gray-50 flex items-center justify-center text-lg font-bold"
                            title={t('app_zoomIn')}
                          >
                            +
                          </button>
                          <button
                            onClick={() => {
                              if (mapRef.current && userLocation) {
                                const currentZoom = mapRef.current.getZoom();
                                mapRef.current.flyTo({
                                  center: [userLocation.longitude, userLocation.latitude],
                                  zoom: Math.max(currentZoom - 1, 1), // Min zoom 1
                                  duration: 500
                                });
                              }
                            }}
                            className="w-8 h-8 bg-white border border-gray-300 shadow-md hover:bg-gray-50 flex items-center justify-center text-lg font-bold"
                            title={t('app_zoomOut')}
                          >
                            −
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                      <div className="text-center">
                        <div className="text-gray-500 mb-2">{t('app_loadingMap')}</div>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            )}
            
            {/* Suggested Meal Controls - only show when in suggested meal mode */}
            {suggestedMealMode && (
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                    {t('app_chooseYourPreference')}
                  </h3>
                  
                  {/* Preference Dropdown */}
                  <div className="mb-4">
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={suggestedMealPreference}
                      onChange={(e) => setSuggestedMealPreference(e.target.value as 'normal' | 'nicer' | 'cheaper')}
                    >
                      <option value="normal">{t('app_prefNormal')}</option>
                      <option value="nicer">{t('app_prefNicer')}</option>
                      <option value="cheaper">{t('app_prefCheaper')}</option>
                    </select>
                  </div>
                  
                  {/* Start Button */}
                  <button 
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed"
                    onClick={performSuggestedMealSearch}
                    disabled={suggestedMealLoading}
                  >
                    {suggestedMealLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        {t('app_searching')}
                      </div>
                    ) : (
                      t('app_startSearch')
                    )}
                  </button>
                  
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {t('app_blueCircleNote')}
                  </p>
                </div>
                
                {/* Search Results */}
                {suggestedMealResults.length > 0 && (
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">
                      {t('app_found')} {suggestedMealResults.length} {t('app_restaurantsWord')}
                      {suggestedMealPreference === 'nicer' && ` ${t('app_higherQuality')}`}
                      {suggestedMealPreference === 'cheaper' && ` ${t('app_budgetFriendly')}`}
                    </h4>
                    
                    <div className="max-h-64 overflow-y-auto space-y-4">
                      {(() => {
                        // Group restaurants by charger
                        const restaurantsByCharger = suggestedMealResults.reduce((acc: any, restaurant: any) => {
                          const chargerId = restaurant.nearCharger?.id || 'unknown';
                          if (!acc[chargerId]) {
                            acc[chargerId] = {
                              charger: restaurant.nearCharger,
                              restaurants: []
                            };
                          }
                          acc[chargerId].restaurants.push(restaurant);
                          return acc;
                        }, {});

                        return Object.values(restaurantsByCharger).slice(0, 10).map((group: any, groupIndex: number) => (
                          <div key={group.charger?.id || groupIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                            {/* Charger Header with Link */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center">
                                <span className="text-red-600 mr-2">⚡</span>
                                <h5 className="font-semibold text-gray-900 text-sm">
                                  {group.charger?.name || t('app_unknownCharger')}
                                </h5>
                              </div>
                              <button
                                onClick={async () => {
                                  if (group.charger) {
                                    // Exit suggested meal mode
                                    exitSuggestedMealMode();
                                    // Load all restaurants for this charger
                                    await searchRestaurantsNearCharger(group.charger);
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs font-medium underline"
                              >
                                {t('app_seeAllRestaurants')}
                              </button>
                            </div>

                            {/* Restaurants for this charger */}
                            <div className="space-y-2">
                              {group.restaurants.map((restaurant: any, index: number) => (
                                <button
                                  key={restaurant.place_id || index}
                                  className="w-full bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  onClick={async () => {
                                    if (restaurant.nearCharger) {
                                      // Exit suggested meal mode
                                      exitSuggestedMealMode();
                                      // Load all restaurants for this charger
                                      await searchRestaurantsNearCharger(restaurant.nearCharger);
                                    }
                                  }}
                                >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium text-gray-900 truncate">
                                {restaurant.name}
                              </h5>
                              <div className="flex items-center mt-1">
                                {restaurant.rating && (
                                  <div className="flex items-center mr-2">
                                    <span className="text-yellow-400">★</span>
                                    <span className="text-sm text-gray-600 ml-1">
                                      {restaurant.rating.toFixed(1)}
                                    </span>
                                    {restaurant.user_ratings_total && (
                                      <span className="text-xs text-gray-400 ml-1">
                                        ({restaurant.user_ratings_total})
                                      </span>
                                    )}
                                  </div>
                                )}
                                {restaurant.price_level && (
                                  <div className="flex items-center">
                                    <span className="text-sm text-gray-600">
                                      {'$'.repeat(restaurant.price_level)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {restaurant.vicinity && (
                                <p className="text-xs text-gray-500 mt-1 truncate">
                                  {restaurant.vicinity}
                                </p>
                              )}
                            </div>
                            {restaurant.distanceToCharger && (
                              <div className="ml-2 text-xs text-gray-500">
                                {(restaurant.distanceToCharger * 1000).toFixed(0)}m
                              </div>
                            )}
                          </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}

                      {suggestedMealResults.length > 10 && (
                        <div className="text-center py-2">
                          <p className="text-sm text-gray-500">
                            {t('app_and')} {suggestedMealResults.length - 10} {t('app_moreRestaurants')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* No Results Message */}
                {!suggestedMealLoading && suggestedMealResults.length === 0 && suggestedMealPreference && (
                  <div className="mt-6 border-t border-gray-200 pt-4 text-center">
                    <p className="text-gray-500">
                      {t('app_noRestaurantsInArea')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
          <LoginModal
            isOpen={showLoginModal}
            onClose={() => closeLoginModal()}
            onLogin={(e) => handleLogin(e, closeLoginModal)}
            onSwitchToRegister={() => {
              closeLoginModal();
              openRegisterModal();
            }}
            form={loginForm}
            setForm={setLoginForm}
            error={authError}
          />
        </Suspense>
      )}

      {/* Register Modal */}
      {showRegisterModal && (
        <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
          <RegisterModal
            isOpen={showRegisterModal}
            onClose={() => closeRegisterModal()}
            onRegister={(e) => handleRegister(e, closeRegisterModal)}
            onSwitchToLogin={() => {
              closeRegisterModal();
              openLoginModal();
            }}
            form={registerForm}
            setForm={setRegisterForm}
            error={authError}
            couponValidation={couponValidation}
            validateCoupon={validateCoupon}
          />
        </Suspense>
      )}

      {/* Settings Modal */}
      <Suspense fallback={<div></div>}>
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={closeSettingsModal}
          onSaveSettings={(e) => saveSettings(e, user, setUser, closeSettingsModal, checkAuthStatus, getNetworks)}
          settings={settings}
          setSettings={setSettings}
          error={settingsError}
          user={user}
          setUser={setUser}
          priceRange={priceRange}
          updatePriceRange={updatePriceRange}
          maxRestaurants={maxRestaurants}
          updateMaxRestaurants={updateMaxRestaurants}
          onManageSubscription={() => {
            fetchAvailableTiers();
            openSubscriptionManagementModal();
          }}
          onRestaurantPreferences={() => {
            openRestaurantPreferencesModal();
          }}
        />
      </Suspense>

       {/* FAQ Modal */}
       <Suspense fallback={<div></div>}>
         <FAQModal 
           isOpen={showFAQModal} 
           onClose={closeFAQModal}
         />
       </Suspense>

       {/* Learn More Modal */}
       {showLearnMoreModal && (
         <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black bg-opacity-60">
           <div className="bg-white rounded-lg p-8 w-full max-w-4xl mx-4 relative max-h-[90vh] overflow-y-auto">
             <button
               className="absolute top-3 right-3 bg-gray-200 hover:bg-gray-300 rounded-full px-4 py-2 text-2xl font-bold z-10"
               onClick={() => closeLearnMoreModal()}
               aria-label={t('app_close')}
             >
               ×
             </button>
             <h2 className="text-3xl font-bold mb-6 text-center">{t('app_learnTitle')}</h2>

             <div className="space-y-6 text-gray-700 leading-relaxed">
               <div>
                 <h3 className="text-xl font-semibold mb-3 text-primary-600">{t('app_learnRealityTitle')}</h3>
                 <p className="text-base">
                   {t('app_learnRealityText')}
                 </p>
               </div>

               <div>
                 <h3 className="text-xl font-semibold mb-3 text-primary-600">{t('app_learnPreOrderTitle')}</h3>
                 <p className="text-base">
                   {t('app_learnPreOrderText')}
                 </p>
               </div>

               <div>
                 <h3 className="text-xl font-semibold mb-3 text-primary-600">{t('app_learnHowTitle')}</h3>
                 <div className="space-y-3">
                   <div className="flex items-start space-x-3">
                     <div className="bg-primary-100 text-primary-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                     <div>
                       <strong>{t('app_learnStep1Title')}</strong> {t('app_learnStep1Text')}
                     </div>
                   </div>
                   <div className="flex items-start space-x-3">
                     <div className="bg-primary-100 text-primary-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                     <div>
                       <strong>{t('app_learnStep2Title')}</strong> {t('app_learnStep2Text')}
                     </div>
                   </div>
                   <div className="flex items-start space-x-3">
                     <div className="bg-primary-100 text-primary-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                     <div>
                       <strong>{t('app_learnStep3Title')}</strong> {t('app_learnStep3Text')}
                     </div>
                   </div>
                 </div>
               </div>
               
               <div>
                 <h3 className="text-xl font-semibold mb-3 text-primary-600">{t('app_learnVisionTitle')}</h3>
                 <p className="text-base">
                   {t('app_learnVisionText')}
                 </p>
               </div>
               
               <div className="bg-primary-50 p-4 rounded-lg">
                 <h4 className="font-semibold text-primary-700 mb-2">{t('app_learnBenefitsTitle')}</h4>
                 <ul className="space-y-1 text-sm">
                   <li>• <strong>{t('app_benefitTimeTitle')}</strong> {t('app_benefitTimeText')}</li>
                   <li>• <strong>{t('app_benefitChoicesTitle')}</strong> {t('app_benefitChoicesText')}</li>
                   <li>• <strong>{t('app_benefitStressTitle')}</strong> {t('app_benefitStressText')}</li>
                   <li>• <strong>{t('app_benefitJourneyTitle')}</strong> {t('app_benefitJourneyText')}</li>
                   <li>• <strong>{t('app_benefitFutureTitle')}</strong> {t('app_benefitFutureText')}</li>
                 </ul>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Disclaimer Modal */}
       {showDisclaimerModal && (
         <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black bg-opacity-60">
           <div className="bg-white rounded-lg p-8 w-full max-w-2xl mx-4 relative max-h-[90vh] overflow-y-auto">
             <button
               className="absolute top-3 right-3 bg-gray-200 hover:bg-gray-300 rounded-full px-4 py-2 text-2xl font-bold z-10"
               onClick={() => closeDisclaimerModal()}
               aria-label={t('app_close')}
             >
               ×
             </button>
             <div className="text-center mb-6">
               <div className="text-4xl mb-4">⚠️</div>
               <h2 className="text-3xl font-bold text-red-600">{t('disclaimerTitle')}</h2>
             </div>
             
             <div className="space-y-6 text-gray-700 leading-relaxed">
               <div className="bg-red-50 border-l-4 border-red-400 p-4">
                 <h3 className="text-xl font-semibold mb-3 text-red-700">{t('importantSafetyNotice')}</h3>
                 <p className="text-base mb-4">
                   <strong>{t('disclaimerText')}</strong>
                 </p>
               </div>
               
               <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                 <h4 className="font-semibold text-blue-700 mb-2">{t('safeUsageGuidelines')}</h4>
                 <ul className="space-y-2 text-sm">
                   <li>• <strong>{t('onlyUseWhenParked')}</strong></li>
                   <li>• <strong>{t('passengerUse')}</strong></li>
                   <li>• <strong>{t('safeEnvironment')}</strong></li>
                   <li>• <strong>{t('followTrafficLaws')}</strong></li>
                   <li>• <strong>{t('stayAlert')}</strong></li>
                 </ul>
               </div>
               
               <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                 <label className="flex items-center space-x-3 cursor-pointer">
                   <input
                     type="checkbox"
                     id="doNotShowAgain"
                     className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                   />
                   <span className="text-sm font-medium text-yellow-800">
                     {t('doNotShowAgain')}
                   </span>
                 </label>
                 <p className="text-xs text-yellow-700 mt-2">
                   {isAuthenticated 
                     ? t('settingsNote')
                     : t('browserNote')
                   }
                 </p>
               </div>
               
               <div className="text-center space-y-4">
                 <p className="text-sm text-gray-600">
                   {t('disclaimerAcknowledgment')}
                 </p>
                 <div className="flex flex-col sm:flex-row gap-4 justify-center">
                   <button
                     onClick={() => closeDisclaimerModal()}
                     className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                   >
                     {t('cancel')}
                   </button>
                   <button
                     onClick={() => {
                       const doNotShowAgain = (document.getElementById('doNotShowAgain') as HTMLInputElement)?.checked || false;
                       if (isAuthenticated) {
                         acceptDisclaimer(doNotShowAgain);
                       } else {
                         // For non-authenticated users, save preference to localStorage
                         if (doNotShowAgain) {
                           localStorage.setItem('disclaimerAccepted', 'true');
                           localStorage.setItem('disclaimerDoNotShowAgain', 'true');
                         }
                         closeDisclaimerModal();
                         handleGeolocate();
                       }
                     }}
                     className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors"
                   >
                     {t('iUnderstand')}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Restaurant Preferences Modal */}
       {showRestaurantPreferencesModal && (
         <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
           <RestaurantPreferencesModal
             isOpen={showRestaurantPreferencesModal}
             onClose={() => closeRestaurantPreferencesModal()}
             priceRange={priceRange}
             updatePriceRange={updatePriceRange}
           />
         </Suspense>
       )}

       {/* Subscription Management Modal */}
       {showSubscriptionManagementModal && (
         <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
           <SubscriptionManagementModal
             isOpen={showSubscriptionManagementModal}
             onClose={() => closeSubscriptionManagementModal()}
             userSubscription={userSubscription}
             pricingPlans={pricingPlans}
             onPlanChange={handleSubscriptionChange}
             currency={pricingCurrency}
           />
         </Suspense>
       )}

       {/* Tier Change Confirmation Modal */}
       {showTierChangeConfirmationModal && pendingTierChange && (
         <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
           <TierChangeConfirmationModal
             isOpen={showTierChangeConfirmationModal}
             onClose={() => {
               closeTierChangeConfirmationModal();
               setPendingTierChange(null);
             }}
             onConfirm={handleTierChangeConfirm}
             currentTier={pendingTierChange.currentTier}
             newTier={pendingTierChange.newTier}
             isUpgrade={pendingTierChange.isUpgrade}
             isLoading={isTierChangeLoading}
           />
         </Suspense>
       )}

       {/* Report Non-Restaurant Modal */}
       {showReportNonRestaurantModal && restaurantToReport && (
         <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
           <ReportNonRestaurantModal
             isOpen={showReportNonRestaurantModal}
             onClose={() => {
               closeReportNonRestaurantModal();
               setRestaurantToReport(null);
             }}
             restaurant={restaurantToReport}
             onSubmitReport={submitNonRestaurantReport}
             t={t}
           />
         </Suspense>
       )}


       {/* Contact Modal */}
       {showContactModal && (
         <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
           <ContactModal
             isOpen={showContactModal}
             onClose={() => closeContactModal()}
             onSubmit={handleContactSubmit}
             form={contactForm}
             setForm={setContactForm}
             status={contactStatus}
           />
         </Suspense>
       )}


       {/* Pricing Modal */}
       <Suspense fallback={null}>
         <PricingModal
           isOpen={showPricingModal}
           onClose={closePricingModal}
           onSubscribe={handleSubscribe}
           pricingPlans={pricingPlans}
           isLoadingSubscription={isLoadingSubscription}
           subscriptionInfo={subscriptionInfo}
           currency={pricingCurrency}
         />
       </Suspense>

       {/* Proximity Notification Stack */}
       <Suspense fallback={null}>
         <ProximityNotificationStack
           notifications={proximityNotifications.notifications}
           onDismiss={dismissProximityNotification}
           onNavigate={handleNavigateToCharger}
           onWaitOneHour={waitOneHourProximity}
           onWaitTwoHours={waitTwoHoursProximity}
           onTurnOff={turnOffProximityNotifications}
           t={t}
           collapsedNotifications={collapsedNotifications}
           onToggleCollapse={toggleNotificationCollapse}
           userLocation={userLocation}
           unitSystem={settings.unit_system || 'metric'}
           userPreferences={userPreferences}
         />
       </Suspense>

       {/* Suggested Meal Modal */}
       {showSuggestedMealModal && (
         <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"><div className="bg-white rounded-lg p-8">{t('app_loading')}</div></div>}>
           <SuggestedMealModal
             isOpen={showSuggestedMealModal}
             onClose={closeSuggestedMealModal}
             user={user}
             userPreferences={userPreferences}
             userSubscription={userSubscription}
             userLocation={userLocation}
             t={t}
             mapboxToken={mapboxToken}
             superchargers={superchargers}
             viewport={viewport}
             setViewport={setViewport}
             selectedCharger={selectedCharger}
             setSelectedCharger={setSelectedCharger}
             nearbyRestaurants={nearbyRestaurants}
             setNearbyRestaurants={setNearbyRestaurants}
             handleSuperchargerClick={handleSuperchargerClick}
             calculateOptimalZoom={calculateOptimalZoom}
             setPreviousViewport={setPreviousViewport}
           />
         </Suspense>
       )}

       {/* Charger Search Modal */}
       <ChargerSearchModal
         isOpen={chargerSearchModalOpen}
         onClose={() => setChargerSearchModalOpen(false)}
         onSelectCharger={handleChargerSelection}
       />

       {/* Cookie Consent Modal */}
       <CookieConsentModal
         isOpen={showCookieConsentModal}
         onAccept={acceptCookies}
         onDecline={declineCookies}
       />


     </div>
   );
 }

export default App;
